import React, { useState, useRef, useEffect, useLayoutEffect, memo, useCallback, useMemo } from 'react';
import FieldImageGallery from './FieldImageGallery';
import FieldVideo, { FieldVideoPlayer } from './FieldVideo';
import AudioUploader from './AudioUploader';
import AudioVersions from './AudioVersions';
import ImageModal from './ImageModal';
import { useCharacterCounter } from '../hooks/useCharacterCounter';
import CharacterCounter from './CharacterCounter';
import { fastScrollToElement } from '../utils/scroll';
import { lensShows, lensMatchesPoint, lensPointsActive, lensTags, lensAssignee, LENS_ASSIGNEE_ME, PREDEFINED_TAGS } from '../lensSections';
import { placeCaret } from '../utils/caret';
import { TracePanel } from './Pipeline/PipelinePanel';
import { StageBadge } from './Pipeline/pipelineStage';
import { useModels, modelKey, ModelPicker } from './Pipeline/modelInfo';
import { backdropClose } from './backdrop';

// A memoized audio player that won't re-mount (and therefore won't reset playback)
// unless its source actually changes.
export const StableAudioPlayer = memo(({ src }) => {
  const audioRef = useRef(null);

  // Imperatively set the src only if it really changed so that the element
  // itself is preserved across re-renders and playback is not interrupted.
  useEffect(() => {
    if (audioRef.current) {
      const currentSrc = audioRef.current.getAttribute('src');
      if (currentSrc !== src) {
        const wasPlaying = !audioRef.current.paused;
        audioRef.current.setAttribute('src', src);
        if (wasPlaying) {
          audioRef.current.play().catch(() => {});
        }
      }
    }
  }, [src]);

  return (
    <audio
      ref={audioRef}
      controls
      style={{ width: '100%' }}
      preload="metadata"
      crossOrigin="anonymous"
      src={src}
    />
  );
}, (prevProps, nextProps) => prevProps.src === nextProps.src);

// Helper function to escape HTML
const escapeHtml = (unsafe) => {
  if (typeof unsafe !== 'string') return '';
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
};

// Fields that can carry a video clip — must match VIDEO_FIELD_TYPES on the server.
const VIDEO_FIELDS = ['description', 'task', 'after_message'];
// Photos of how the place itself looks. Stored as appearance_<field>; the app
// shows them ahead of the field's own images on the test stand only.
const APPEARANCE_FIELDS = ['description', 'task', 'after_message'];

const formatBytes = (bytes, decimals = 2) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const ALL_RECIPIENTS = 'All';

const getTagStyle = (tag) => {
  const lowerCaseTag = (tag || '').toLowerCase();
  if (lowerCaseTag === 'achtung') {
    return { backgroundColor: '#e74c3c', color: 'white', borderColor: '#c0392b' };
  }
  if (lowerCaseTag === 'good') {
    return { backgroundColor: '#2ecc71', color: 'white', borderColor: '#27ae60' };
  }
  if (lowerCaseTag === 'acceptable') {
    return { backgroundColor: '#f39c12', color: 'white', borderColor: '#d68910' };
  }
  if (lowerCaseTag === 'ai-approved') {
    // Set by the pipeline when the critic model approves the point.
    return { backgroundColor: '#188038', color: 'white', borderColor: '#0f6d2c' };
  }
  return {};
};

const formatAssignees = (assignees) => (assignees && assignees.length ? assignees.join(', ') : '');

// Small popover to pick a tag's recipient(s): "All" or any subset of logins.
const AssigneePopover = ({ users = [], value = [], onConfirm, onClose }) => {
  const [sel, setSel] = useState(value);
  const ref = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose]);

  const toggle = (u) => {
    if (u === ALL_RECIPIENTS) { setSel([ALL_RECIPIENTS]); return; }
    setSel(prev => {
      const without = prev.filter(x => x !== ALL_RECIPIENTS);
      return without.includes(u) ? without.filter(x => x !== u) : [...without, u];
    });
  };

  return (
    <div className="assignee-popover" ref={ref}>
      <div className="assignee-popover-title">Assign to</div>
      <div className="assignee-options">
        <button
          type="button"
          className={`assignee-option ${sel.includes(ALL_RECIPIENTS) ? 'on' : ''}`}
          onClick={() => toggle(ALL_RECIPIENTS)}
        >
          {ALL_RECIPIENTS}
        </button>
        {users.map(u => (
          <button
            key={u}
            type="button"
            className={`assignee-option ${sel.includes(u) ? 'on' : ''}`}
            onClick={() => toggle(u)}
          >
            {u}
          </button>
        ))}
      </div>
      <div className="assignee-popover-actions">
        <button type="button" className="comment-mini-button" onClick={onClose}>Cancel</button>
        <button type="button" className="comment-mini-button save" onClick={() => onConfirm(sel)}>OK</button>
      </div>
    </div>
  );
};

const TagsEditor = memo(({ tags = [], allQuestTags = [], users = [], assignees = {}, onAddTag, onRemoveTag, onReassignTag }) => {
  const [newTag, setNewTag] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  // The tag whose recipients are being picked: { tag, mode: 'add'|'reassign' }.
  const [picking, setPicking] = useState(null);
  const editorRef = useRef(null);
  const inputRef = useRef(null);

  const chooseTagToAdd = (tagToAdd) => {
    const tag = (tagToAdd || newTag).trim();
    if (!tag) return;
    setNewTag('');
    if (tags.includes(tag)) {
      // Already present — let them re-pick recipients instead.
      setPicking({ tag, mode: 'reassign' });
      return;
    }
    setPicking({ tag, mode: 'add' });
  };

  const confirmPick = (sel) => {
    if (!picking) return;
    if (picking.mode === 'add') onAddTag(picking.tag, sel);
    else onReassignTag(picking.tag, sel);
    setPicking(null);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      chooseTagToAdd();
    }
  };

  const handleFocus = () => setShowSuggestions(true);
  const handleBlur = (e) => {
    if (e.relatedTarget && editorRef.current?.contains(e.relatedTarget)) return;
    setShowSuggestions(false);
  };

  const customSuggestions = allQuestTags.filter(t => !tags.includes(t) && !PREDEFINED_TAGS.includes(t));
  const predefinedSuggestions = PREDEFINED_TAGS.filter(t => !tags.includes(t));

  return (
    <div className="tags-editor" ref={editorRef} onBlur={handleBlur} tabIndex="-1">
      <div className="tags-list">
        {tags.map(tag => {
          const asg = assignees[tag] || [];
          return (
            <span key={tag} className="tag-chip" style={getTagStyle(tag)}>
              <span className="tag-chip-label">{tag}</span>
              <button
                type="button"
                className="tag-assignee"
                onClick={() => setPicking({ tag, mode: 'reassign' })}
                title="Assign to…"
              >
                {asg.length ? `→ ${formatAssignees(asg)}` : '→ ?'}
              </button>
              <button onClick={() => onRemoveTag(tag)} className="remove-tag" title="Remove tag">&times;</button>
            </span>
          );
        })}
      </div>
      <div className="add-tag-controls">
        <input
          ref={inputRef}
          type="text"
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder="Add a tag..."
          className="add-tag-input"
        />
        <button onClick={() => chooseTagToAdd()} className="add-tag-button" title="Add tag">+</button>
      </div>
      {picking && (
        <AssigneePopover
          users={users}
          value={picking.mode === 'reassign' ? (assignees[picking.tag] || []) : []}
          onConfirm={confirmPick}
          onClose={() => setPicking(null)}
        />
      )}
      {showSuggestions && (
        <div className="tags-suggestions">
          {predefinedSuggestions.length > 0 && (
            <div className="predefined-tags">
              <span className="predefined-tags-label">Quick add: </span>
              {predefinedSuggestions.map(tag => (
                <button key={tag} onMouseDown={(e) => e.preventDefault()} onClick={() => chooseTagToAdd(tag)} className="predefined-tag-button" style={getTagStyle(tag)}>
                  {tag}
                </button>
              ))}
            </div>
          )}
          {customSuggestions.length > 0 && (
             <div className="custom-tags-suggestions">
                <span className="predefined-tags-label">Used elsewhere: </span>
                {customSuggestions.map(tag => (
                    <button key={tag} onMouseDown={(e) => e.preventDefault()} onClick={() => chooseTagToAdd(tag)} className="predefined-tag-button">
                        {tag}
                    </button>
                ))}
             </div>
          )}
        </div>
      )}
    </div>
  );
});

/** Download path suffix + button label for stored originals (WAV, MP3, …). */
function originalAudioExtensionFromEntry(audio) {
  if (!audio) return 'wav';
  const mime = (audio.mime_type || '').toLowerCase();
  if (mime === 'audio/mpeg' || mime === 'audio/mp3') return 'mp3';
  if (mime === 'audio/wav' || mime === 'audio/wave' || mime === 'audio/x-wav') return 'wav';
  if (audio.filename && typeof audio.filename === 'string') {
    const m = audio.filename.match(/\.([^.]+)$/i);
    if (m) {
      const e = m[1].toLowerCase();
      if (e === 'mp3') return 'mp3';
      if (e === 'wav') return 'wav';
    }
  }
  return 'wav';
}

function originalAudioFormatLabelFromEntry(audio) {
  const ext = originalAudioExtensionFromEntry(audio);
  if (ext === 'mp3') return 'MP3';
  if (ext === 'wav') return 'WAV';
  return ext.toUpperCase();
}

const EditableFieldComponent = memo(({
  locationId, field, value, multiline,
  isEditingThisField,
  images, appearanceImages, audioUrl, audioExists,
  videos, onVideoChangedCallback, onEditVideoCaptionCallback, onReorderVideosCallback,
  downloadAudioUrl,
  originalAudioExists, originalAudioSize, downloadOriginalAudioUrl, originalAudioFormatLabel = 'WAV',
  selectedQuestId,
  onStartEditCallback,
  onSaveEditCallback,
  onImageUploadedCallback,
  onAudioUploadedCallback,
  onDeleteImageCallback,
  onEditImageCaptionCallback,
  onToggleImageBlurCallback,
  onReorderImagesCallback,
  onDeleteAudioCallback,
  onVersionsChangedCallback,
  onThumbnailClickCallback,
  onGenerateVoiceoverCallback,
  audioSize,
  isReadOnly,
  maxLength,
}) => {
  const editableRef = useRef(null);
  const clickPointRef = useRef(null);
  const charCount = useCharacterCounter(value, isEditingThisField, editableRef);
  // Lives here, not in FieldVideo: the chips sit in the middle column while the
  // player they toggle is rendered below the row. Holds the id of the unfolded
  // clip, or null.
  const [openVideoId, setOpenVideoId] = useState(null);

  // Which of the three media columns this field can carry. A comment has none;
  // the voiceover column also appears wherever audio already exists, so a
  // recording on an unexpected field never becomes invisible.
  const hasImageColumn = field !== 'comment';
  const hasVideoColumn = VIDEO_FIELDS.includes(field);
  const hasAudioColumn = audioExists || field === 'description' || field === 'after_message';
  const openVideo = hasVideoColumn && openVideoId != null
    ? (videos || []).find(v => v.id === openVideoId) || null
    : null;

  useEffect(() => {
    if (isEditingThisField && editableRef.current) {
      // Determine the correct innerHTML based on the current `value` prop
      let currentPropDisplayValue = value === '' ? '\u00A0' : value;
      currentPropDisplayValue = escapeHtml(currentPropDisplayValue);

      // Forcefully set the innerHTML to the value from props
      // This overwrites any direct DOM modifications (e.g., by browser extensions)
      editableRef.current.innerHTML = currentPropDisplayValue;

      // Drop the caret where the user clicked (not at the end), and don't
      // scroll - see placeCaret. clickPointRef is set on the click that opens editing.
      placeCaret(editableRef.current, clickPointRef.current);
      clickPointRef.current = null;
    }
  }, [isEditingThisField, value]); // Added `value` to the dependency array

  const handleBlur = () => {
    if (editableRef.current) {
      const content = editableRef.current.innerText.trim();
      const newValue = content === '\u00A0' ? '' : content; // Handle non-breaking space
      onSaveEditCallback(locationId, field, newValue);
    }
  };

  const handleKeyDownEvent = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (editableRef.current) {
        const content = editableRef.current.innerText.trim();
        const newValue = content === '\u00A0' ? '' : content; // Handle non-breaking space
        onSaveEditCallback(locationId, field, newValue);
      }
    }
  };

  const handleClick = (e) => {
    if (!isEditingThisField && !isReadOnly) {
      // Remember where the click landed so the effect can put the caret there.
      clickPointRef.current = { x: e.clientX, y: e.clientY };
      onStartEditCallback(locationId, field);
    }
  };

  let displayValue = value === '' ? '\u00A0' : value; // Handle non-breaking space for display
  displayValue = escapeHtml(displayValue);
  const fieldId = `location-${locationId}-${field}`;

  return (
    <div className="editable-field-container">
      <div
        id={fieldId}
        className={`editable-field ${multiline ? 'multiline' : ''} ${isEditingThisField ? 'editing' : ''} ${field === 'comment' ? 'comment-field' : ''}`}
        contentEditable={isEditingThisField}
        suppressContentEditableWarning={true}
        ref={editableRef}
        onBlur={isEditingThisField ? handleBlur : undefined}
        onKeyDown={isEditingThisField ? handleKeyDownEvent : undefined}
        onClick={handleClick}
        dangerouslySetInnerHTML={{ __html: displayValue }}
      />
      <CharacterCounter count={charCount} maxLength={maxLength} />
      {(hasImageColumn || hasVideoColumn || hasAudioColumn) && (
      <div className="field-actions">
        {/* Three fixed columns — images, video, voiceover — so the same kind of
            media always sits in the same place as the eye travels down a
            location. A column a field can't carry stays empty rather than
            letting the others slide over. */}
        <div className="field-media-row">
        <div className="field-media-col">
        {/* Image section - ordered gallery with per-image controls (excluding comment field) */}
        {hasImageColumn && (
          <div className="field-actions-images">
            <span className="field-media-label">Images</span>
            <FieldImageGallery
              images={images || []}
              locationId={locationId}
              fieldType={field}
              questId={selectedQuestId}
              onImageUploaded={() => onImageUploadedCallback(locationId, field)}
              onDeleteImage={(image) => onDeleteImageCallback(image)}
              onEditCaption={(image) => onEditImageCaptionCallback(image)}
              onToggleBlur={onToggleImageBlurCallback ? (image) => onToggleImageBlurCallback(image) : undefined}
              onReorder={(orderedIds) => onReorderImagesCallback(locationId, field, orderedIds)}
              onThumbnailClick={(index) => onThumbnailClickCallback(images || [], index)}
              disabled={isReadOnly}
            />
            {APPEARANCE_FIELDS.includes(field) && (
              <>
                <span className="field-media-label" title="Shown only on app-test, ahead of the images above">Appearance · test stand only</span>
                <FieldImageGallery
                  images={appearanceImages || []}
                  locationId={locationId}
                  fieldType={`appearance_${field}`}
                  questId={selectedQuestId}
                  onImageUploaded={() => onImageUploadedCallback(locationId, `appearance_${field}`)}
                  onDeleteImage={(image) => onDeleteImageCallback(image)}
                  onEditCaption={(image) => onEditImageCaptionCallback(image)}
                  onToggleBlur={onToggleImageBlurCallback ? (image) => onToggleImageBlurCallback(image) : undefined}
                  onReorder={(orderedIds) => onReorderImagesCallback(locationId, `appearance_${field}`, orderedIds)}
                  onThumbnailClick={(index) => onThumbnailClickCallback(appearanceImages || [], index)}
                  disabled={isReadOnly}
                />
              </>
            )}
          </div>
        )}
        </div>
        <div className="field-media-col">
        {/* Video section - just the chip; the player unfolds under the row */}
        {hasVideoColumn && (
          <>
            <span className="field-media-label">Video</span>
            <FieldVideo
              videos={videos}
              locationId={locationId}
              fieldType={field}
              questId={selectedQuestId}
              onChanged={onVideoChangedCallback}
              onEditCaption={(clip) => onEditVideoCaptionCallback(clip)}
              onReorder={(orderedIds) => onReorderVideosCallback(locationId, field, orderedIds)}
              disabled={isReadOnly}
              openVideoId={openVideoId}
              onOpenChange={setOpenVideoId}
            />
          </>
        )}
        </div>
        <div className="field-media-col">
        {/* Audio section - player, upload, delete (only for description and after_message) */}
        {hasAudioColumn && (
          <div className="field-actions-audio">
            <span className="field-media-label">Voiceover</span>
            {audioExists && (
              <div className="audio-player-container">
                <StableAudioPlayer src={audioUrl} />
              </div>
            )}
            <div className="audio-buttons">
              {(field === 'description' || field === 'after_message') && (
                <>
                  <AudioUploader
                    locationId={locationId}
                    fieldType={field}
                    onAudioUploaded={(audUrl, originalSize, compressedSize, originalMimeType) =>
                      onAudioUploadedCallback(locationId, field, audUrl, originalSize, compressedSize, originalMimeType)}
                    existingAudioUrl={audioUrl}
                    questId={selectedQuestId}
                    disabled={isReadOnly}
                  />
                  <button
                    className="generate-voiceover-button"
                    onClick={() => onGenerateVoiceoverCallback(locationId, field)}
                    title="Create a voiceover for this text"
                    disabled={isReadOnly}
                  >
                    🎙️
                  </button>
                </>
              )}
              {audioExists && (
                <>
                  {/* Кнопки для скачивания аудио */}
                  <div className="audio-download-container">
                    <a
                      href={downloadAudioUrl}
                      download
                      className="download-audio-link button-style"
                      title={`Download compressed audio (OGG, ${formatBytes(audioSize)})`}
                    >
                      OGG ⬇ {audioSize > 0 && <span className="file-size-inline">({formatBytes(audioSize)})</span>}
                    </a>
                    {originalAudioExists && (
                      <a
                        href={downloadOriginalAudioUrl}
                        download
                        className="download-audio-link button-style original"
                        title={`Download original audio (${originalAudioFormatLabel}, ${formatBytes(originalAudioSize)})`}
                      >
                        {originalAudioFormatLabel} ⬇ {originalAudioSize > 0 && <span className="file-size-inline">({formatBytes(originalAudioSize)})</span>}
                      </a>
                    )}
                  </div>

                  {/* Кнопка удаления */}
                  <button
                    className="delete-audio-button"
                    onClick={() => onDeleteAudioCallback(locationId, field)}
                    title="Delete this audio"
                    disabled={isReadOnly}
                  >
                    🗑️
                  </button>
                </>
              )}
            </div>
            <AudioVersions
              locationId={locationId}
              fieldType={field}
              audioUrl={audioUrl}
              isReadOnly={isReadOnly}
              onVersionsChanged={() => onVersionsChangedCallback && onVersionsChangedCallback()}
            />
          </div>
        )}
        </div>
        </div>
        {/* The clip plays under the whole row, where it has the full width */}
        {openVideo && <FieldVideoPlayer video={openVideo} />}
      </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Gallery arrays are rebuilt every render; compare them by content.
  const imagesKey = (imgs) => (imgs || []).map((img) => `${img.id}:${img.size}:${img.blurred ? 1 : 0}:${img.caption || ''}`).join(',');
  const videosKey = (vids) => (vids || []).map((v) => `${v.id}:${v.uploaded_at}:${v.caption || ''}`).join(',');
  // Custom comparison function for React.memo
  return prevProps.locationId === nextProps.locationId &&
         prevProps.field === nextProps.field &&
         prevProps.value === nextProps.value &&
         prevProps.multiline === nextProps.multiline &&
         prevProps.isEditingThisField === nextProps.isEditingThisField &&
         imagesKey(prevProps.images) === imagesKey(nextProps.images) &&
         imagesKey(prevProps.appearanceImages) === imagesKey(nextProps.appearanceImages) &&
         videosKey(prevProps.videos) === videosKey(nextProps.videos) &&
         prevProps.onVideoChangedCallback === nextProps.onVideoChangedCallback &&
         prevProps.onEditVideoCaptionCallback === nextProps.onEditVideoCaptionCallback &&
         prevProps.onReorderVideosCallback === nextProps.onReorderVideosCallback &&
         prevProps.audioUrl === nextProps.audioUrl &&
         prevProps.audioExists === nextProps.audioExists &&
         prevProps.selectedQuestId === nextProps.selectedQuestId &&
         prevProps.onStartEditCallback === nextProps.onStartEditCallback &&
         prevProps.onSaveEditCallback === nextProps.onSaveEditCallback &&
         prevProps.onImageUploadedCallback === nextProps.onImageUploadedCallback &&
         prevProps.onAudioUploadedCallback === nextProps.onAudioUploadedCallback &&
         prevProps.onDeleteImageCallback === nextProps.onDeleteImageCallback &&
         prevProps.onEditImageCaptionCallback === nextProps.onEditImageCaptionCallback &&
         prevProps.onToggleImageBlurCallback === nextProps.onToggleImageBlurCallback &&
         prevProps.onReorderImagesCallback === nextProps.onReorderImagesCallback &&
         prevProps.onDeleteAudioCallback === nextProps.onDeleteAudioCallback &&
         prevProps.onVersionsChangedCallback === nextProps.onVersionsChangedCallback &&
         prevProps.onThumbnailClickCallback === nextProps.onThumbnailClickCallback &&
         prevProps.onGenerateVoiceoverCallback === nextProps.onGenerateVoiceoverCallback &&
         prevProps.downloadAudioUrl === nextProps.downloadAudioUrl &&
         prevProps.originalAudioExists === nextProps.originalAudioExists &&
         prevProps.originalAudioSize === nextProps.originalAudioSize &&
         prevProps.downloadOriginalAudioUrl === nextProps.downloadOriginalAudioUrl &&
         prevProps.originalAudioFormatLabel === nextProps.originalAudioFormatLabel &&
         prevProps.audioSize === nextProps.audioSize &&
         prevProps.maxLength === nextProps.maxLength &&
         prevProps.isReadOnly === nextProps.isReadOnly;
});

// Human-friendly timestamp for a comment bubble: just the time (24h, with
// seconds) if it's today, otherwise DD.MM.YYYY, HH:MM:SS. Formatted manually so
// it doesn't depend on the browser locale (which could fall back to a 12h clock).
const formatCommentTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return time;
  const date = `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  return `${date}, ${time}`;
};

// Quick references a comment can point at — the location's own fields. The
// token is what's stored in the message body (plain text, export-friendly);
// the label is shown both on the composer chip and on the clickable reference.
const REFERENCE_FIELDS = [
  { key: 'description', token: '@description', label: 'Description' },
  { key: 'task', token: '@task', label: 'Task' },
  { key: 'after_message', token: '@after_message', label: 'After Message' },
  { key: 'branching_message', token: '@branching_message', label: 'Branching Message' },
];
// Besides field tokens, a message can reference a stored voice-over version
// as @audio:<versionId> — inserted via the "@" autocomplete in the composer,
// rendered as a play chip with an inline player.
const REF_TOKEN_RE = /@(description|task|after_message|branching_message)\b|@audio:(\d+)\b/g;

// Filenames of generated versions are all alike, so a version is displayed by
// its creation time (a custom label wins when the user renamed the version).
const versionStamp = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const versionDisplayName = (v) => v.label || versionStamp(v.uploaded_at) || v.filename || `#${v.id}`;

// Scroll the list to a given field of a location and briefly flash it.
const scrollToLocationField = (locationId, field) => {
  const el = document.getElementById(`location-${locationId}-${field}`);
  if (!el) return;
  fastScrollToElement(el);
  el.classList.add('field-ref-flash');
  setTimeout(() => el.classList.remove('field-ref-flash'), 1200);
};

// Render a message body, turning @description / @task / @after_message tokens
// into clickable chips that scroll to that field of this location, and
// @audio:<id> tokens into play chips (audioCtx supplies the version lookup and
// the currently playing token; it's null while versions are still loading).
const renderCommentBody = (text, locationId, audioCtx) => {
  const nodes = [];
  let lastIndex = 0;
  let m;
  REF_TOKEN_RE.lastIndex = 0;
  while ((m = REF_TOKEN_RE.exec(text)) !== null) {
    if (m.index > lastIndex) nodes.push(text.slice(lastIndex, m.index));
    if (m[1]) {
      const field = m[1];
      const meta = REFERENCE_FIELDS.find(f => f.key === field);
      nodes.push(
        <button
          key={`ref-${m.index}`}
          type="button"
          className="comment-ref-chip"
          onClick={() => scrollToLocationField(locationId, field)}
          title={`Scroll to ${meta.label}`}
        >
          {meta.label}
        </button>
      );
    } else {
      const id = Number(m[2]);
      const v = audioCtx && audioCtx.map ? audioCtx.map.get(id) : null;
      // Playback is keyed by the exact occurrence (message + position), so
      // the same version mentioned twice never plays two players at once.
      const occKey = `${audioCtx ? audioCtx.msgKey : ''}:${m.index}`;
      const playing = !!v && audioCtx.playingId === occKey;
      nodes.push(
        <button
          key={`audio-${m.index}`}
          type="button"
          className={`comment-ref-chip audio${v ? '' : ' missing'}`}
          disabled={!v}
          onClick={() => v && audioCtx.onToggle(occKey)}
          title={v
            ? `${playing ? 'Hide the player for' : 'Play'} this voice-over version (${REFERENCE_FIELDS.find(f => f.key === v.field)?.label || v.field})`
            : (audioCtx && audioCtx.loaded ? 'Voice-over version not found (deleted?)' : 'Loading voice-over versions…')}
        >
          {playing ? '⏸' : '▶'} {v ? versionDisplayName(v) : `version #${id}`}
        </button>
      );
      if (playing) {
        nodes.push(
          <audio
            key={`audio-player-${m.index}`}
            className="comment-audio-player"
            controls
            autoPlay
            src={`/api/audio/${locationId}/${v.field}?uuid=${audioCtx.locationUuid}&questId=${audioCtx.questId}&table=quests_test&versionId=${id}`}
          />
        );
      }
    }
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
};

// A comment body that clamps to the first few lines by default and reveals the
// rest with a quick max-height slide. The "Show more" toggle appears only when
// the content is actually taller than the clamp. `deps` re-measures when the
// underlying body text changes.
const COLLAPSED_LINES = 3;
const CollapsibleCommentBody = ({ children, deps }) => {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflow, setOverflow] = useState(false);
  const [collapsedH, setCollapsedH] = useState(0);
  // null => no max-height cap (natural height); a number => capped in px.
  const [maxH, setMaxH] = useState(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const cs = window.getComputedStyle(el);
    let lh = parseFloat(cs.lineHeight);
    if (!lh || Number.isNaN(lh)) lh = parseFloat(cs.fontSize) * 1.4;
    const clamp = Math.round(lh * COLLAPSED_LINES);
    const full = el.scrollHeight;
    const tall = full > clamp + 2;
    setCollapsedH(clamp);
    setOverflow(tall);
    // Reset to collapsed whenever the content changes.
    setExpanded(false);
    setMaxH(tall ? clamp : null);
  }, [deps]);

  const expand = () => {
    const el = ref.current;
    setMaxH(el ? el.scrollHeight : collapsedH);
    setExpanded(true);
  };
  const collapse = () => {
    const el = ref.current;
    // Give the transition a concrete start height before shrinking.
    setMaxH(el ? el.scrollHeight : collapsedH);
    requestAnimationFrame(() => {
      setExpanded(false);
      setMaxH(collapsedH);
    });
  };
  const onTransitionEnd = () => {
    // Once fully open, drop the cap so later growth (e.g. an audio player) fits.
    if (expanded) setMaxH(null);
  };

  return (
    <>
      <div
        ref={ref}
        className={`comment-body${overflow ? ' clampable' : ''}`}
        style={overflow && maxH != null ? { maxHeight: maxH } : undefined}
        onTransitionEnd={onTransitionEnd}
      >
        {children}
      </div>
      {overflow && (
        <button
          type="button"
          className={`comment-body-toggle${expanded ? ' expanded' : ''}`}
          onClick={expanded ? collapse : expand}
          aria-label={expanded ? 'Collapse' : 'Expand'}
          title={expanded ? 'Collapse' : 'Expand'}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.4"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </>
  );
};

// Textarea with an "@" autocomplete. The suggestion list opens when the caret
// sits right after "@query" at a word start, filters as the user types, and
// inserts the picked token. Suggestions are the location's field references
// plus its stored voice-over versions (loaded lazily via loadAudioOptions the
// first time the list opens). Keyboard: ↑/↓ to move, Enter/Tab to pick,
// Escape to close; other keys fall through to onSubmitKeyDown.
const MentionTextarea = ({ inputRef, className, placeholder, rows, autoFocus, value, setValue, onSubmitKeyDown, loadAudioOptions }) => {
  const [menu, setMenu] = useState(null); // { query, start } | null
  const [audioOptions, setAudioOptions] = useState(null); // null = not loaded yet
  const [highlight, setHighlight] = useState(0);

  const refreshMenu = (el) => {
    if (!el) { setMenu(null); return; }
    const caret = el.selectionStart;
    if (el.selectionEnd !== caret) { setMenu(null); return; }
    const before = el.value.slice(0, caret);
    const m = before.match(/(?:^|\s)@([\w:]*)$/);
    if (!m) { setMenu(null); return; }
    setMenu((prev) => {
      const next = { query: m[1].toLowerCase(), start: caret - m[1].length - 1 };
      if (prev && prev.query === next.query && prev.start === next.start) return prev;
      return next;
    });
  };

  // Load the voice-over options once, the first time the menu opens.
  useEffect(() => {
    if (menu && audioOptions === null && loadAudioOptions) {
      let cancelled = false;
      loadAudioOptions().then((opts) => { if (!cancelled) setAudioOptions(opts); });
      return () => { cancelled = true; };
    }
  }, [menu, audioOptions, loadAudioOptions]);

  const matches = (opt, q) => !q
    || opt.token.toLowerCase().includes(q)
    || opt.label.toLowerCase().includes(q)
    || (opt.sub || '').toLowerCase().includes(q);
  const options = menu ? [
    ...REFERENCE_FIELDS.map((f) => ({ token: f.token, icon: '↪', label: f.label, sub: null })),
    ...(audioOptions || []),
  ].filter((o) => matches(o, menu.query)) : [];
  const loading = !!menu && audioOptions === null && !!loadAudioOptions;
  const open = !!menu && (options.length > 0 || loading);
  const active = Math.min(highlight, Math.max(0, options.length - 1));

  const pick = (opt) => {
    const el = inputRef.current;
    const caret = el ? el.selectionStart : value.length;
    const before = value.slice(0, menu.start);
    const after = value.slice(caret);
    const insert = opt.token + (/^\s/.test(after) ? '' : ' ');
    setValue(before + insert + after);
    setMenu(null);
    const pos = (before + insert).length;
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(pos, pos);
      }
    });
  };

  const handleKeyDown = (e) => {
    if (open) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((active + 1) % Math.max(1, options.length)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((active - 1 + Math.max(1, options.length)) % Math.max(1, options.length)); return; }
      if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
        if (options[active]) { e.preventDefault(); pick(options[active]); return; }
      }
      if (e.key === 'Escape') { e.preventDefault(); setMenu(null); return; }
    }
    if (onSubmitKeyDown) onSubmitKeyDown(e);
  };

  return (
    <div className="mention-wrap">
      <textarea
        ref={inputRef}
        className={className}
        placeholder={placeholder}
        rows={rows}
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => { setValue(e.target.value); refreshMenu(e.target); setHighlight(0); }}
        onSelect={(e) => refreshMenu(e.target)}
        onKeyDown={handleKeyDown}
        onBlur={() => setMenu(null)}
      />
      {open && (
        <ul className="mention-menu">
          {options.map((o, i) => (
            <li
              key={o.token}
              className={`mention-item${i === active ? ' active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); pick(o); }}
              onMouseEnter={() => setHighlight(i)}
            >
              <span className="mention-icon">{o.icon}</span>
              <span className="mention-label">{o.label}</span>
              {o.sub && <span className="mention-sub">{o.sub}</span>}
            </li>
          ))}
          {loading && <li className="mention-loading">Loading voice-over versions…</li>}
        </ul>
      )}
    </div>
  );
};

// A row of chips that insert a field reference into the textarea being edited.
const ReferenceChips = ({ onInsert }) => (
  <div className="comment-composer-chips">
    {REFERENCE_FIELDS.map((f) => (
      <button
        key={f.key}
        type="button"
        className="comment-chip"
        onClick={() => onInsert(f.token)}
        title={`Insert a reference to ${f.label}`}
      >
        @ {f.label}
      </button>
    ))}
  </div>
);

// Modal for a 'gen' version event: shows the exact snapshot the model produced
// (the point's texts at that moment) plus its reasoning trace. The trace is not
// in the thread payload (stripped to stay light) — fetch the full comment meta
// on open.
const genModalOverlay = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const genModalBox = {
  background: 'var(--bg, #fff)', color: 'var(--fg, #111)', width: 'min(860px, 92vw)',
  maxHeight: '88vh', overflow: 'auto', borderRadius: 8, padding: 20,
  boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
};
const GenVersionModal = ({ questId, comment, onClose }) => {
  const [meta, setMeta] = useState(comment.meta || {});
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/quest/${questId}/comment/${comment.id}`, { credentials: 'include' });
        if (r.ok) {
          const data = await r.json();
          if (!cancelled && data && data.meta) setMeta(data.meta);
        }
      } catch (e) { /* keep the light meta we already have */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [questId, comment.id]);

  const v = (meta && meta.version) || {};
  const noTask = !!(v.is_no_task != null ? v.is_no_task
    : v.no_task != null ? v.no_task
    : v.is_view_only != null ? v.is_view_only : v.view_only);
  const trace = Array.isArray(meta && meta.trace) ? meta.trace : [];
  const field = (label, val) => (val ? (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', opacity: 0.6, marginBottom: 2 }}>{label}</div>
      <div style={{ whiteSpace: 'pre-wrap' }}>{val}</div>
    </div>
  ) : null);

  return (
    <div style={genModalOverlay} {...backdropClose(onClose)}>
      <div className="pl-modal" style={genModalBox} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>
            Version · {(meta && meta.model) || 'model'}
            {comment.author ? ` · ${comment.author}` : ''}
            {comment.created_at ? ` · ${formatCommentTime(comment.created_at)}` : ''}
          </h3>
          <button onClick={onClose}>✕</button>
        </div>
        {field('Description', v.description)}
        {!noTask && field('Task', v.task)}
        {!noTask && field('Answer', v.answer)}
        {field('Hint', v.hint)}
        {!noTask && field('After message', v.after_message)}
        {noTask && <div style={{ opacity: 0.6, marginBottom: 10 }}>No-task point (view only).</div>}
        <h4 style={{ margin: '14px 0 4px' }}>Reasoning trace</h4>
        {loading
          ? <div style={{ opacity: 0.5 }}>Loading trace…</div>
          : (trace.length
              ? <TracePanel runId={null} live={false} seed={trace} onDone={() => {}} />
              : <div style={{ opacity: 0.5 }}>No trace stored for this version.</div>)}
      </div>
    </div>
  );
};

// A location's comment thread rendered as a chat: bubbles with author + time,
// own messages on the right (editable/deletable), others on the left.
// `messages` are real rows from the quest_comments table; `legacyComment` is the
// old single free-text comment from quest_data, shown as one read-only bubble
// only when the thread is otherwise empty (covers pre-migration / imported data).
const CommentThread = ({ messages, legacyComment, currentUsername, isReadOnly, locationId, locationUuid, questId, onAdd, onEdit, onDelete }) => {
  const [versionModal, setVersionModal] = useState(null);
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const composerRef = useRef(null);
  const editRef = useRef(null);

  // Voice-over versions of this location — the data behind @audio: mentions.
  // Loaded once, lazily: when the "@" menu first opens, or when a rendered
  // message already contains an @audio: token.
  const audioVersionsPromise = useRef(null);
  const [audioVersions, setAudioVersions] = useState(null); // null = not loaded
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const loadAudioVersions = useCallback(() => {
    if (!audioVersionsPromise.current) {
      audioVersionsPromise.current = Promise.all(REFERENCE_FIELDS.map(async (f) => {
        try {
          const r = await fetch(`/api/audio-versions/${locationUuid}/${f.key}`, { credentials: 'include' });
          if (!r.ok) return [];
          const rows = await r.json();
          return rows.map((v) => ({ ...v, field: f.key }));
        } catch (e) {
          return [];
        }
      })).then((lists) => {
        const all = lists.flat();
        setAudioVersions(all);
        return all;
      });
    }
    return audioVersionsPromise.current;
  }, [locationUuid]);

  // Mention options: newest first, shown by creation time (labels are only
  // set when the user renamed a version, generated filenames say nothing).
  const loadAudioOptions = useCallback(async () => {
    const all = await loadAudioVersions();
    return [...all]
      .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at))
      .map((v) => ({
        token: `@audio:${v.id}`,
        icon: '🎙',
        label: versionDisplayName(v),
        sub: `${(REFERENCE_FIELDS.find((f) => f.key === v.field) || {}).label || v.field}${v.is_active ? ' · active' : ''}`,
      }));
  }, [loadAudioVersions]);

  const audioMap = useMemo(() => {
    const map = new Map();
    (audioVersions || []).forEach((v) => map.set(v.id, v));
    return map;
  }, [audioVersions]);
  const audioCtx = {
    map: audioVersions === null ? null : audioMap,
    loaded: audioVersions !== null,
    playingId: playingAudioId,
    onToggle: (id) => setPlayingAudioId((cur) => (cur === id ? null : id)),
    locationUuid,
    questId,
  };

  // Insert a reference token at the caret of a textarea (with tidy spacing).
  const insertReferenceInto = (ref, value, setValue) => (token) => {
    const ta = ref.current;
    let start = value.length;
    let end = value.length;
    if (ta) { start = ta.selectionStart; end = ta.selectionEnd; }
    const before = value.slice(0, start);
    const after = value.slice(end);
    const spaceBefore = before && !/\s$/.test(before) ? ' ' : '';
    const spaceAfter = after && !/^\s/.test(after) ? ' ' : '';
    const insert = `${spaceBefore}${token}${spaceAfter}`;
    setValue(before + insert + after);
    const caret = (before + insert).length;
    requestAnimationFrame(() => {
      if (ref.current) {
        ref.current.focus();
        ref.current.setSelectionRange(caret, caret);
      }
    });
  };

  const realMessages = messages || [];
  const displayMessages = realMessages.length > 0
    ? realMessages
    : (legacyComment
        ? [{ id: 'legacy', author: null, body: legacyComment, created_at: null, edited_at: null, legacy: true }]
        : []);

  // A thread that already references audio needs the version data to render
  // the chips (label by creation time, play URL).
  const hasAudioRefs = displayMessages.some((m) => m.body && m.body.includes('@audio:'));
  useEffect(() => {
    if (hasAudioRefs) loadAudioVersions();
  }, [hasAudioRefs, loadAudioVersions]);

  // Cluster items by time: a gap under 5 minutes from the previous item reads as
  // the same burst (tight spacing), a longer gap separates bursts (loose).
  const TIGHT_GAP_MS = 5 * 60 * 1000;
  const spacingClassFor = (i) => {
    if (i === 0) return 'first';
    const prev = displayMessages[i - 1].created_at ? new Date(displayMessages[i - 1].created_at).getTime() : NaN;
    const cur = displayMessages[i].created_at ? new Date(displayMessages[i].created_at).getTime() : NaN;
    if (isNaN(prev) || isNaN(cur)) return 'loose';
    return Math.abs(cur - prev) < TIGHT_GAP_MS ? 'tight' : 'loose';
  };

  const handleSend = () => {
    const text = draft.trim();
    if (!text) return;
    onAdd(locationUuid, text);
    setDraft('');
  };
  const handleComposerKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startEdit = (m) => { setEditingId(m.id); setEditDraft(m.body); };
  const cancelEdit = () => { setEditingId(null); setEditDraft(''); };
  const saveEdit = (m) => {
    const text = editDraft.trim();
    if (!text) return;
    if (text !== m.body) onEdit(locationUuid, m.id, text);
    cancelEdit();
  };
  const handleEditKeyDown = (e, m) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(m); }
    else if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
  };

  return (
    <div className="comment-thread">
      <div className="comment-thread-messages">
        {displayMessages.length === 0 && (
          <div className="comment-thread-empty">No messages yet.</div>
        )}
        {displayMessages.map((m, i) => {
          if (m.kind === 'gen') {
            const model = (m.meta && m.meta.model) || 'model';
            const isRevise = (m.meta && m.meta.node) === '2b';
            return (
              <div key={m.id} className={`comment-system-row ${spacingClassFor(i)}`}>
                <span
                  className="comment-system-line"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setVersionModal(m)}
                  title="Show this version and its reasoning trace"
                >
                  🤖 <b>{model}</b> {isRevise ? 'revised' : 'wrote'} a version — <span style={{ textDecoration: 'underline' }}>view</span>
                  {m.author ? <> · {m.author}</> : null}
                  {m.created_at && <span className="comment-system-time"> · {formatCommentTime(m.created_at)}</span>}
                </span>
              </div>
            );
          }
          if (m.kind === 'tag') {
            const action = m.meta && m.meta.action;
            const tag = (m.meta && m.meta.tag) || m.body;
            const asg = (m.meta && m.meta.assignees) || [];
            const verb = action === 'remove' ? 'removed' : (action === 'assign' ? 'reassigned' : 'set');
            return (
              <div key={m.id} className={`comment-system-row ${spacingClassFor(i)}`}>
                <span className="comment-system-line">
                  🏷 <b>{m.author || '—'}</b> {verb}{' '}
                  <span className="comment-system-tag" style={getTagStyle(tag)}>{tag}</span>
                  {asg.length > 0 && <> → <b>{asg.join(', ')}</b></>}
                  {m.created_at && <span className="comment-system-time"> · {formatCommentTime(m.created_at)}</span>}
                </span>
              </div>
            );
          }
          const isLegacy = !m.author;
          const isMine = !isLegacy && m.author === currentUsername;
          const canManage = isMine && !isReadOnly && !m.legacy;
          return (
            <div key={m.id} className={`comment-bubble-row ${isMine ? 'mine' : 'theirs'} ${spacingClassFor(i)}`}>
              <div className={`comment-bubble ${isMine ? 'mine' : 'theirs'} ${isLegacy ? 'legacy' : ''}`}>
                <div className="comment-bubble-meta">
                  <span className="comment-author">{isLegacy ? '—' : m.author}</span>
                  {m.created_at && <span className="comment-time">{formatCommentTime(m.created_at)}</span>}
                  {m.edited_at && (
                    <span className="comment-edited">(edited {formatCommentTime(m.edited_at)})</span>
                  )}
                </div>
                {editingId === m.id ? (
                  <div className="comment-edit">
                    <MentionTextarea
                      inputRef={editRef}
                      className="comment-edit-input"
                      value={editDraft}
                      setValue={setEditDraft}
                      onSubmitKeyDown={(e) => handleEditKeyDown(e, m)}
                      loadAudioOptions={loadAudioOptions}
                      autoFocus
                      rows={2}
                    />
                    <ReferenceChips onInsert={insertReferenceInto(editRef, editDraft, setEditDraft)} />
                    <div className="comment-edit-actions">
                      <button type="button" className="comment-mini-button save" onClick={() => saveEdit(m)}>Save</button>
                      <button type="button" className="comment-mini-button" onClick={cancelEdit}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <CollapsibleCommentBody deps={m.body}>
                    {renderCommentBody(m.body, locationId, { ...audioCtx, msgKey: m.id })}
                  </CollapsibleCommentBody>
                )}
                {canManage && editingId !== m.id && (
                  <div className="comment-bubble-actions">
                    <button type="button" onClick={() => startEdit(m)} title="Edit message">✏️</button>
                    <button type="button" onClick={() => onDelete(locationUuid, m.id)} title="Delete message">🗑️</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {!isReadOnly && (
        <div className="comment-composer">
          <div className="comment-composer-row">
            <MentionTextarea
              inputRef={composerRef}
              className="comment-composer-input"
              placeholder="Write a message…  (Enter to send, Shift+Enter — new line, @ — reference)"
              value={draft}
              setValue={setDraft}
              onSubmitKeyDown={handleComposerKeyDown}
              loadAudioOptions={loadAudioOptions}
              rows={2}
            />

            <button
              type="button"
              className="comment-composer-send"
              onClick={handleSend}
              disabled={!draft.trim()}
            >
              Send
            </button>
          </div>
          <ReferenceChips onInsert={insertReferenceInto(composerRef, draft, setDraft)} />
        </div>
      )}
      {versionModal && (
        <GenVersionModal questId={questId} comment={versionModal} onClose={() => setVersionModal(null)} />
      )}
    </div>
  );
};

const API_BASE_URL = '';

// Fixed once per page load — see getAudioUrl: a media URL must never change
// between renders, so the fallback cache-buster is a constant, not Date.now().
const PAGE_LOAD_STAMP = Date.now();

const LocationsList = ({
  locations, 
  selectedLocationId, 
  onLocationSelect,
  onQuestSelect, 
  selectedQuestId, 
  questsList = [],
  currentQuestCity = null,
  onMoveLocationToQuest,
  onLocationUpdate,
  saveStatus,
  onLocationReorder,
  onAddLocation,
  onRemoveLocation,
  onLogout,
  onShowSpeechGeneration,
  onExportLocationMarkdown,
  audioRefreshToken,
  isReadOnly,
  selectedTable,
  commentsByLocation = {},
  currentUsername,
  onAddComment,
  onEditComment,
  onDeleteComment,
  usernames = [],
  tagAssigneesByLocation = {},
  onAddTag,
  onRemoveTag,
  onReassignTag,
  // Optional: render a per-location generation strip (pipeline drafts only).
  renderPipelineControls = null,
  // Optional: uuid -> pipeline point, for the stage badge in the sidebar list.
  pipelinePointsByUuid = null,
  // View-only filter: which card sections are visible and which points pass the
  // tag filter ({} = show everything). See lensSections.js.
  lens = {},
  // Optional: uuid -> {open, total} of the point's Tasks cards, and the link to
  // the Tasks screen filtered to that point (a chip next to the title).
  taskCountsByLocation = null,
  tasksHref = null,
}) => {
  const lensShow = (key) => lensShows(lens, key);
  // Points filtered out by the Lens tag filter are simply not rendered here (the
  // map keeps them, pale). The original index is preserved so the numbering and
  // the reorder buttons still refer to the real position in the quest.
  const lensPasses = (location) =>
    lensMatchesPoint(lens, location, tagAssigneesByLocation[location.uuid], currentUsername);
  const lensFilteringPoints = lensPointsActive(lens);
  const lensVisibleLocations = lensFilteringPoints ? locations.filter(lensPasses) : locations;
  // Human-readable summary of the active point filter, for the list header.
  const lensFilterNote = [
    lensTags(lens).length > 0 ? `tag: ${lensTags(lens).join(', ')}` : null,
    lensAssignee(lens)
      ? `assigned to: ${lensAssignee(lens) === LENS_ASSIGNEE_ME ? (currentUsername || 'me') : lensAssignee(lens)}`
      : null,
  ].filter(Boolean).join(' · ');
  const [editingField, setEditingField] = useState(null);
  const [newAnswer, setNewAnswer] = useState('');
  // Per-point "expand answers" in flight (uuid -> bool). The pipeline writes the
  // new variants server-side and they arrive via the location_field_updated SSE
  // event, so we only track the spinner here.
  const [answerGenBusy, setAnswerGenBusy] = useState({});
  const pipelineModels = useModels();          // to name the model on the ✨ button
  const handleGenerateAnswerVariants = async (location) => {
    if (!location.uuid || !selectedQuestId) return;
    setAnswerGenBusy((b) => ({ ...b, [location.uuid]: true }));
    try {
      const res = await fetch(`/api/pipeline/quest/${selectedQuestId}/answer-variants/${location.uuid}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: '{}',
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);
    } catch (e) {
      window.alert(`Could not expand answers: ${e.message}`);
    } finally {
      setAnswerGenBusy((b) => ({ ...b, [location.uuid]: false }));
    }
  };
  const [draggedAnswerIndex, setDraggedAnswerIndex] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);
  const [modalImage, setModalImage] = useState(null);
  const [questImages, setQuestImages] = useState([]);
  const [questVideos, setQuestVideos] = useState([]);
  const [questAudioFiles, setQuestAudioFiles] = useState([]);
  const [questOriginalAudioFiles, setQuestOriginalAudioFiles] = useState([]);
  const [showMoveToPosition, setShowMoveToPosition] = useState(null); // Track which location's position dropdown is shown
  const [copiedUuid, setCopiedUuid] = useState(null); // Состояние для отслеживания скопированного UUID
  const [copiedCoordinates, setCopiedCoordinates] = useState(null);
  const editableRef = useRef(null);
  const newAnswerRef = useRef(null);
  const locationsListRef = useRef(null);
  const [copiedLocationId, setCopiedLocationId] = useState(null); // Для всплывающего уведомления копирования JSON
  const copiedToastTimeoutRef = useRef(null);
  // Which location's export menu (JSON / Markdown) is open. Null = none.
  const [exportMenuForId, setExportMenuForId] = useState(null);
  // Close the per-location export menu on any outside click.
  useEffect(() => {
    if (exportMenuForId == null) return undefined;
    const onDown = (e) => {
      if (!e.target.closest || !e.target.closest('.location-export-menu-wrapper')) setExportMenuForId(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [exportMenuForId]);
  const [collapsedComments, setCollapsedComments] = useState({}); // Состояние для свернутых комментариев
  const [collapsedAnswers, setCollapsedAnswers] = useState({}); // Состояние для свернутых ответов
  const [editingLocationCoords, setEditingLocationCoords] = useState(null); // ID of location whose coords are being edited
  const [coordsInput, setCoordsInput] = useState(''); // Text input for coordinates
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth <= 1024
  ); // Collapsed by default on mobile (map top, list bottom layout)

  const allUniqueTags = useMemo(() => {
    const tagsSet = new Set();
    locations.forEach(location => {
        if (location.tags) {
            location.tags.forEach(tag => {
                tagsSet.add(tag);
            });
        }
    });
    return Array.from(tagsSet);
  }, [locations]);

  const questsEligibleForMove = useMemo(() => {
    if (!questsList || !selectedQuestId) return [];
    return questsList.filter((q) => {
      if (q.quest_id === selectedQuestId) return false;
      // Same city as the open quest (quests without a city always qualify).
      if (!currentQuestCity) return true;
      return q.city == null || q.city === currentQuestCity;
    });
  }, [questsList, selectedQuestId, currentQuestCity]);

  const [moveToQuestSelections, setMoveToQuestSelections] = useState({});

  // Function to toggle the collapsed state of a comment
  const toggleCommentCollapse = (locationId) => {
    setCollapsedComments(prev => {
      // Determine the current state. If undefined (or true), consider it collapsed.
      const isCurrentlyCollapsed = prev[locationId] !== false;
      return {
        ...prev,
        // Invert the state
        [locationId]: !isCurrentlyCollapsed,
      };
    });
  };

  // Check if the comment is collapsed (collapsed by default)
  const isCommentCollapsed = (locationId) => {
    // Only explicitly expanded if false
    return collapsedComments[locationId] !== false;
  };

  const toggleAnswersCollapse = (locationId) => {
    setCollapsedAnswers(prev => {
      const isCurrentlyCollapsed = prev[locationId] !== false;
      return {
        ...prev,
        [locationId]: !isCurrentlyCollapsed,
      };
    });
  };

  const isAnswersCollapsed = (locationId) => {
    return collapsedAnswers[locationId] !== false;
  };

  const handleBranchUpdate = (locationId, branchIndex, field, value) => {
    const location = locations.find(loc => loc.id === locationId);
    if (!location || isReadOnly) return;

    const updatedBranches = [...(location.branches || [])];
    const branchToUpdate = { ...(updatedBranches[branchIndex] || {}) };
    branchToUpdate[field] = value;
    updatedBranches[branchIndex] = branchToUpdate;

    onLocationUpdate(locationId, 'branches', updatedBranches);
  };

  const handleAddBranch = (locationId) => {
    const location = locations.find(loc => loc.id === locationId);
    if (!location || isReadOnly) return;
    const updatedBranches = [...(location.branches || [])];
    updatedBranches.push({ text: 'New Branch', next_location_uuid: 'END' });
    onLocationUpdate(locationId, 'branches', updatedBranches);
  };

  const handleRemoveBranch = (locationId, branchIndex) => {
    const location = locations.find(loc => loc.id === locationId);
    if (!location || isReadOnly) return;

    const updatedBranches = [...(location.branches || [])];
    updatedBranches.splice(branchIndex, 1);
    onLocationUpdate(locationId, 'branches', updatedBranches);
  };

  const handleCoordsEditStart = (location) => {
    if (isReadOnly) return;
    setEditingLocationCoords(location.id);
    setCoordsInput(
      location.coordinates && location.coordinates.length === 2
        ? `${location.coordinates[0]}, ${location.coordinates[1]}`
        : ''
    );
  };

  const handleCoordsSave = (locationId) => {
    let processedInput = coordsInput.trim();
    if (processedInput.startsWith('(') && processedInput.endsWith(')')) {
      processedInput = processedInput.substring(1, processedInput.length - 1);
    }
    
    if (processedInput === '') {
      onLocationUpdate(locationId, 'coordinates', []);
      setEditingLocationCoords(null);
      return;
    }

    const parts = processedInput.split(',').map(s => s.trim());
    if (parts.length === 2) {
      const lon = parseFloat(parts[0]);
      const lat = parseFloat(parts[1]);
      if (!isNaN(lon) && !isNaN(lat)) {
        onLocationUpdate(locationId, 'coordinates', [lon, lat]);
        setEditingLocationCoords(null);
        return;
      }
    }

    alert('Invalid coordinates format. Please use "longitude, latitude". The change will not be saved.');
    setEditingLocationCoords(null); // Cancel editing on invalid format
  };

  // Функция для копирования UUID в буфер обмена
  const copyUuidToClipboard = (uuid) => {
    if (!uuid) return;
    
    // Fallback method for clipboard copy
    const fallbackCopyTextToClipboard = (text) => {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      
      // Make the textarea out of viewport
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      let success = false;
      try {
        success = document.execCommand('copy');
      } catch (err) {
        console.error('Fallback: Unable to copy', err);
      }
      
      document.body.removeChild(textArea);
      return success;
    };
    
    if (!navigator.clipboard) {
      // Fallback for browsers that don't support clipboard API
      if (fallbackCopyTextToClipboard(uuid)) {
        setCopiedUuid(uuid);
        setTimeout(() => {
          setCopiedUuid(null);
        }, 2000);
      } else {
        console.error('Failed to copy UUID using fallback method');
      }
      return;
    }
    
    navigator.clipboard.writeText(uuid)
      .then(() => {
        setCopiedUuid(uuid);
        // Сбрасываем состояние через 2 секунды
        setTimeout(() => {
          setCopiedUuid(null);
        }, 2000);
      })
      .catch(err => {
        console.error('Не удалось скопировать UUID:', err);
      });
  };

  // Функция для копирования координат в буфер обмена
  const copyCoordinatesToClipboard = (coordinates) => {
    if (!coordinates || coordinates.length < 2) return;
    
    const coordsText = `(${coordinates[0]}, ${coordinates[1]})`;

    // Fallback method for clipboard copy
    const fallbackCopyTextToClipboard = (text) => {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      
      // Make the textarea out of viewport
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      let success = false;
      try {
        success = document.execCommand('copy');
      } catch (err) {
        console.error('Fallback: Unable to copy', err);
      }
      
      document.body.removeChild(textArea);
      return success;
    };
    
    if (!navigator.clipboard) {
      // Fallback for browsers that don't support clipboard API
      if (fallbackCopyTextToClipboard(coordsText)) {
        setCopiedCoordinates(coordsText);
        setTimeout(() => {
          setCopiedCoordinates(null);
        }, 2000);
      } else {
        console.error('Failed to copy coordinates using fallback method');
      }
      return;
    }
    
    navigator.clipboard.writeText(coordsText)
      .then(() => {
        setCopiedCoordinates(coordsText);
        // Сбрасываем состояние через 2 секунды
        setTimeout(() => {
          setCopiedCoordinates(null);
        }, 2000);
      })
      .catch(err => {
        console.error('Не удалось скопировать координаты:', err);
      });
  };

  // Функция для копирования JSON локации в буфер обмена
  const copyLocationJsonToClipboard = async (location) => {
    if (!location) return;
    
    const locationJson = JSON.stringify(location, null, 2);

    // Fallback method for clipboard copy
    const fallbackCopyTextToClipboard = (text) => {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      
      // Make the textarea out of viewport
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      let success = false;
      try {
        success = document.execCommand('copy');
      } catch (err) {
        console.error('Fallback: Unable to copy', err);
      }
      
      document.body.removeChild(textArea);
      return success;
    };
    
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(locationJson);
        console.log(`Location "${location.name}" JSON copied to clipboard`);
        setCopiedLocationId(location.id);
        if (copiedToastTimeoutRef.current) clearTimeout(copiedToastTimeoutRef.current);
        copiedToastTimeoutRef.current = setTimeout(() => setCopiedLocationId(null), 1200);
      } else {
        // Fallback for browsers that don't support clipboard API
        if (fallbackCopyTextToClipboard(locationJson)) {
          console.log(`Location "${location.name}" JSON copied to clipboard`);
          setCopiedLocationId(location.id);
          if (copiedToastTimeoutRef.current) clearTimeout(copiedToastTimeoutRef.current);
          copiedToastTimeoutRef.current = setTimeout(() => setCopiedLocationId(null), 1200);
        } else {
          throw new Error('Copy command failed');
        }
      }
    } catch (err) {
      console.error('Failed to copy location JSON:', err);
    }
  };

  // Cleanup for toast timeout
  useEffect(() => {
    return () => {
      if (copiedToastTimeoutRef.current) clearTimeout(copiedToastTimeoutRef.current);
    };
  }, []);

  // Fetch all images for the current quest (reusable so gallery edits can
  // refresh the list after upload/delete/reorder).
  const refreshQuestImages = useCallback(async () => {
    if (!selectedQuestId) return;
    try {
      const response = await fetch(`/api/quest-images/${selectedQuestId}?table=${selectedTable}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch quest images');
      }

      const data = await response.json();
      setQuestImages(data);
    } catch (error) {
      console.error('Error fetching quest images:', error);
    }
  }, [selectedQuestId, selectedTable]);

  useEffect(() => {
    refreshQuestImages();
  }, [refreshQuestImages]);

  // A pipeline stage (hints) inserted images server-side — refetch the gallery list.
  useEffect(() => {
    const onImages = () => { refreshQuestImages(); };
    window.addEventListener('qv:images', onImages);
    return () => window.removeEventListener('qv:images', onImages);
  }, [refreshQuestImages]);

  // Videos of the current quest — a handful per (location, field) at most, so
  // this list stays short and a single refetch after upload/delete is enough.
  const refreshQuestVideos = useCallback(async () => {
    if (!selectedQuestId) return;
    try {
      const response = await fetch(`/api/quest-videos/${selectedQuestId}?table=${selectedTable}`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch quest videos');
      setQuestVideos(await response.json());
    } catch (error) {
      console.error('Error fetching quest videos:', error);
    }
  }, [selectedQuestId, selectedTable]);

  useEffect(() => {
    refreshQuestVideos();
  }, [refreshQuestVideos]);

  // Fetch all audio files for the current quest (reusable so the version panel
  // can trigger a refresh after activating/deleting a version).
  const refreshAudioData = useCallback(async () => {
    if (!selectedQuestId) return;
    try {
      const [audioRes, origRes] = await Promise.all([
        fetch(`/api/quest-audio/${selectedQuestId}?table=${selectedTable}`, { credentials: 'include' }),
        fetch(`/api/quest-original-audio/${selectedQuestId}?table=${selectedTable}`, { credentials: 'include' }),
      ]);
      if (audioRes.ok) setQuestAudioFiles(await audioRes.json());
      if (origRes.ok) setQuestOriginalAudioFiles(await origRes.json());
    } catch (error) {
      console.error('Error fetching quest audio files:', error);
    }
  }, [selectedQuestId, selectedTable]);

  useEffect(() => {
    refreshAudioData();
  }, [refreshAudioData, audioRefreshToken]);

  const handleEditStart = useCallback((locationId, field) => {
    if (isReadOnly) return;
    setEditingField(`${locationId}-${field}`);
  }, [isReadOnly]);

  const handleSaveEdit = useCallback((locationId, field, value) => {
    onLocationUpdate(locationId, field, value); // This triggers hasUnsavedChanges -> auto-save in App.js
    setEditingField(null);
    // onSaveClick is implicitly handled by the auto-save triggered by onLocationUpdate
    // The scroll restoration is handled by App.js's saveQuestData
  }, [onLocationUpdate]);

  // Ordered gallery for a specific location and field type: [{ id, url, size, caption, blurred }]
  const getImagesFor = (locationId, fieldType) => {
    // Находим локацию по её ID
    const location = locations.find(loc => loc.id === locationId);
    if (!location || !location.uuid) return [];

    // Server returns the list already sorted by gallery order
    return questImages
      .filter(img => img.location_uuid === location.uuid && img.field_type === fieldType)
      .map(img => ({
        id: img.id,
        size: img.size,
        caption: img.caption,
        blurred: !!img.blurred,
        url: `${API_BASE_URL}/api/image-file/${img.id}`
      }));
  };

  // In gallery order: the server sorts by sort_order, and a local reorder keeps
  // the moved clips in that same relative order (see localHandleReorderVideos).
  const getVideosFor = (locationId, fieldType) => {
    const location = locations.find(loc => loc.id === locationId);
    if (!location || !location.uuid) return [];
    return questVideos.filter(
      v => v.location_uuid === location.uuid && v.field_type === fieldType
    );
  };

  // Check if there's an audio file for a specific location and field type
  const hasAudio = (locationId, fieldType) => {
    const location = locations.find(loc => loc.id === locationId);
    if (!location || !location.uuid) return false;
    return questAudioFiles.some(audio => 
      audio.location_uuid === location.uuid && audio.field_type === fieldType
    );
  };

  const getAudioSize = (locationId, fieldType) => {
    const location = locations.find(loc => loc.id === locationId);
    if (!location || !location.uuid) return null;
    const audio = questAudioFiles.find(audio => 
      audio.location_uuid === location.uuid && audio.field_type === fieldType
    );
    return audio ? audio.size : null;
  };

  // Check if there's an original audio file and get its size
  const hasOriginalAudio = (locationId, fieldType) => {
    const location = locations.find(loc => loc.id === locationId);
    if (!location || !location.uuid) return false;
    return questOriginalAudioFiles.some(audio =>
      audio.location_uuid === location.uuid && audio.field_type === fieldType
    );
  };

  const getOriginalAudioSize = (locationId, fieldType) => {
    const location = locations.find(loc => loc.id === locationId);
    if (!location || !location.uuid) return null;
    const audio = questOriginalAudioFiles.find(audio =>
      audio.location_uuid === location.uuid && audio.field_type === fieldType
    );
    return audio ? audio.size : null;
  };

  const getOriginalAudioEntry = (locationId, fieldType) => {
    const location = locations.find(loc => loc.id === locationId);
    if (!location || !location.uuid) return null;
    return questOriginalAudioFiles.find(audio =>
      audio.location_uuid === location.uuid && audio.field_type === fieldType
    );
  };

  const getOriginalAudioFormatLabel = (locationId, fieldType) =>
    originalAudioFormatLabelFromEntry(getOriginalAudioEntry(locationId, fieldType));

  // Get audio URL for a specific location and field
  const getAudioUrl = (locationId, fieldType) => {
    const location = locations.find(loc => loc.id === locationId);

    // Cache-buster for the <audio> src. It MUST stay identical across re-renders:
    // any change makes the browser drop the loaded file and restart it, so a
    // per-render value (Date.now()) killed playback on every unrelated update —
    // dragging the panel divider, typing, autosave. updated_ms comes from the
    // file's uploaded_at and moves only when the voice-over is really replaced.
    // Fallback for a response without it: one value per page load, plus the
    // refresh token so a fresh upload still bypasses the cache.
    const audioInfo = location && location.uuid
      ? questAudioFiles.find(audio => audio.location_uuid === location.uuid && audio.field_type === fieldType)
      : null;
    const timestamp = audioInfo?.updated_ms || `${PAGE_LOAD_STAMP}-${audioRefreshToken || 0}`;

    if (location && location.uuid) {
      return `${API_BASE_URL}/api/audio/${locationId}/${fieldType}?uuid=${location.uuid}&questId=${selectedQuestId}&table=${selectedTable}&t=${timestamp}`;
    } else {
      // Fallback or error, as UUID is crucial for audio
      console.warn('Cannot get audio URL without location UUID');
      return null;
    }
  };

  // Get download URL для прямого скачивания аудио файла
  const getDownloadAudioUrl = (locationId, fieldType) => {
    const location = locations.find(loc => loc.id === locationId);
    if (location && location.uuid && selectedQuestId) {
      return `${API_BASE_URL}/api/download-audio/${selectedQuestId}/${fieldType}/${location.uuid}.ogg?table=${selectedTable}`;
    } else {
      console.warn('Cannot get download audio URL without location UUID or questId');
      return null;
    }
  };

  // Get download URL for the stored original (WAV, MP3, … — matches DB mime_type)
  const getDownloadOriginalAudioUrl = (locationId, fieldType) => {
    const location = locations.find(loc => loc.id === locationId);
    if (location && location.uuid && selectedQuestId) {
      const ext = originalAudioExtensionFromEntry(getOriginalAudioEntry(locationId, fieldType));
      return `${API_BASE_URL}/api/download-audio/${selectedQuestId}/${fieldType}/${location.uuid}.${ext}?table=${selectedTable}`;
    }
    return null;
  };

  // Uploads append a new row on the server; just refetch the gallery data.
  const localHandleImageUploaded = useCallback(async () => {
    await refreshQuestImages();
  }, [refreshQuestImages]);

  const localHandleAudioUploaded = useCallback(async (locationId, fieldType, audioUrl, originalSize, compressedSize, originalMimeType) => {
    const location = locations.find(loc => loc.id === locationId);
    if (!location || !location.uuid || isReadOnly) return;
    const locationUuid = location.uuid;
    const timestamp = Date.now();

    // Update compressed audio files state with new timestamp
    const newQuestAudioFiles = [...questAudioFiles];
    const existingIndex = newQuestAudioFiles.findIndex(
      audio => audio.location_uuid === locationUuid && audio.field_type === fieldType
    );

    const updatedAudioFile = {
      location_id: locationId.toString(),
      location_uuid: locationUuid,
      field_type: fieldType,
      size: compressedSize, // Use the new size
      updated_at: timestamp
    };

    if (existingIndex >= 0) {
      newQuestAudioFiles[existingIndex] = updatedAudioFile;
    } else {
      newQuestAudioFiles.push(updatedAudioFile);
    }
    setQuestAudioFiles(newQuestAudioFiles);

    // Update original audio files state
    const newQuestOriginalAudioFiles = [...questOriginalAudioFiles];
    const existingOriginalIndex = newQuestOriginalAudioFiles.findIndex(
      audio => audio.location_uuid === locationUuid && audio.field_type === fieldType
    );

    // If originalSize is provided, update/add the record.
    // If originalSize is null, it means an OGG was uploaded directly, so we remove the original.
    if (originalSize !== null && originalSize > 0) {
      const updatedOriginalAudioFile = {
        location_id: locationId.toString(),
        location_uuid: locationUuid,
        field_type: fieldType,
        size: originalSize, // Use the new size
        updated_at: timestamp,
        mime_type: originalMimeType || undefined
      };
      if (existingOriginalIndex >= 0) {
        newQuestOriginalAudioFiles[existingOriginalIndex] = updatedOriginalAudioFile;
      } else {
        newQuestOriginalAudioFiles.push(updatedOriginalAudioFile);
      }
    } else {
      // If an original file existed, remove it from the state
      if (existingOriginalIndex >= 0) {
        newQuestOriginalAudioFiles.splice(existingOriginalIndex, 1);
      }
    }
    setQuestOriginalAudioFiles(newQuestOriginalAudioFiles);

    // Перезапрашиваем данные об аудио файлах для получения актуальных размеров
    try {
      const [audioResponse, originalAudioResponse] = await Promise.all([
        fetch(`/api/quest-audio/${selectedQuestId}?table=${selectedTable}`, {
          credentials: 'include'
        }),
        fetch(`/api/quest-original-audio/${selectedQuestId}?table=${selectedTable}`, {
          credentials: 'include'
        })
      ]);
      
      if (audioResponse.ok) {
        const audioData = await audioResponse.json();
        // Обновляем timestamps для загруженного файла
        const updatedAudio = audioData.map(audio => ({
          ...audio,
          updated_at: audio.location_uuid === locationUuid && audio.field_type === fieldType 
            ? timestamp 
            : (questAudioFiles.find(qa => qa.location_uuid === audio.location_uuid && qa.field_type === audio.field_type)?.updated_at || timestamp)
        }));
        setQuestAudioFiles(updatedAudio);
      }
      
      if (originalAudioResponse.ok) {
        const originalAudioData = await originalAudioResponse.json();
        const updatedOriginalAudio = originalAudioData.map(audio => ({
          ...audio,
          updated_at: audio.location_uuid === locationUuid && audio.field_type === fieldType 
            ? timestamp 
            : (questOriginalAudioFiles.find(qa => qa.location_uuid === audio.location_uuid && qa.field_type === audio.field_type)?.updated_at || timestamp)
        }));
        setQuestOriginalAudioFiles(updatedOriginalAudio);
      }
    } catch (error) {
      console.error('Error refreshing quest audio files:', error);
    }

  }, [questAudioFiles, questOriginalAudioFiles, locations, selectedQuestId, selectedTable, isReadOnly]);

  const localHandleThumbnailClick = useCallback((images, index) => {
    setModalImage({ images, index });
  }, []);

  const localHandleDeleteImage = useCallback(async (image) => {
    if (isReadOnly || !image?.id) return;
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }
    try {
      const response = await fetch(`/api/image-file/${image.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete image');
      }
      setQuestImages(prevImages => prevImages.filter(img => img.id !== image.id));
    } catch (error) {
      console.error('Image deletion error:', error);
      alert(`Failed to delete image: ${error.message}`);
    }
  }, [isReadOnly]);

  // Set or clear an image caption (players see it under the photo in the app).
  const localHandleEditImageCaption = useCallback(async (image) => {
    if (isReadOnly || !image?.id) return;
    const next = window.prompt('Image caption (leave empty to remove):', image.caption || '');
    if (next === null) return; // cancelled
    try {
      const response = await fetch(`/api/image-file/${image.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ caption: next })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update caption');
      }
      const data = await response.json();
      setQuestImages(prevImages =>
        prevImages.map(img => (img.id === image.id ? { ...img, caption: data.caption } : img))
      );
    } catch (error) {
      console.error('Image caption error:', error);
      alert(`Failed to update caption: ${error.message}`);
    }
  }, [isReadOnly]);

  // Flip an image's blur flag: the app shows it blurred until the player taps it.
  const localHandleToggleImageBlur = useCallback(async (image) => {
    if (isReadOnly || !image?.id) return;
    try {
      const response = await fetch(`/api/image-file/${image.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ blurred: !image.blurred })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update blur');
      }
      const data = await response.json();
      setQuestImages(prevImages =>
        prevImages.map(img => (img.id === image.id ? { ...img, blurred: data.blurred } : img))
      );
    } catch (error) {
      console.error('Image blur error:', error);
      alert(`Failed to update blur: ${error.message}`);
    }
  }, [isReadOnly]);

  // Same for a clip: the caption shows under the player, both here and in the app.
  const localHandleEditVideoCaption = useCallback(async (video) => {
    if (isReadOnly || !video?.id) return;
    const next = window.prompt('Video caption (leave empty to remove):', video.caption || '');
    if (next === null) return; // cancelled
    try {
      const response = await fetch(`/api/video-file/${video.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ caption: next })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update caption');
      }
      const data = await response.json();
      setQuestVideos(prevVideos =>
        prevVideos.map(v => (v.id === video.id ? { ...v, caption: data.caption } : v))
      );
    } catch (error) {
      console.error('Video caption error:', error);
      alert(`Failed to update caption: ${error.message}`);
    }
  }, [isReadOnly]);

  // Same shape as localHandleReorderImages: the list is reordered locally at
  // once and refetched only if the server refuses.
  const localHandleReorderVideos = useCallback(async (locationId, fieldType, orderedIds) => {
    if (isReadOnly) return;
    const location = locations.find(loc => loc.id === locationId);
    if (!location || !location.uuid) return;
    const locationUuid = location.uuid;

    setQuestVideos(prevVideos => {
      const rest = prevVideos.filter(v =>
        !(v.location_uuid === locationUuid && v.field_type === fieldType)
      );
      const own = prevVideos.filter(v =>
        v.location_uuid === locationUuid && v.field_type === fieldType
      );
      const byId = new Map(own.map(v => [v.id, v]));
      const reordered = orderedIds.map(id => byId.get(id)).filter(Boolean);
      return [...rest, ...reordered];
    });

    try {
      const response = await fetch('/api/videos/reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ locationUuid, fieldType, orderedIds })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reorder videos');
      }
    } catch (error) {
      console.error('Video reorder error:', error);
      refreshQuestVideos();
    }
  }, [isReadOnly, locations, refreshQuestVideos]);

  // Persist a new gallery order; update local state optimistically and
  // re-sync from the server on failure.
  const localHandleReorderImages = useCallback(async (locationId, fieldType, orderedIds) => {
    if (isReadOnly) return;
    const location = locations.find(loc => loc.id === locationId);
    if (!location || !location.uuid) return;
    const locationUuid = location.uuid;

    setQuestImages(prevImages => {
      const rest = prevImages.filter(img =>
        !(img.location_uuid === locationUuid && img.field_type === fieldType)
      );
      const pair = prevImages.filter(img =>
        img.location_uuid === locationUuid && img.field_type === fieldType
      );
      const byId = new Map(pair.map(img => [img.id, img]));
      const reordered = orderedIds.map(id => byId.get(id)).filter(Boolean);
      return [...rest, ...reordered];
    });

    try {
      const response = await fetch('/api/images/reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ locationUuid, fieldType, orderedIds })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reorder images');
      }
    } catch (error) {
      console.error('Image reorder error:', error);
      refreshQuestImages();
    }
  }, [isReadOnly, locations, refreshQuestImages]);

  const localHandleDeleteAudio = useCallback(async (locationId, fieldType) => {
    const location = locations.find(loc => loc.id === locationId);
    if (!location || !location.uuid || isReadOnly) {
      console.error("Cannot delete audio: Location or UUID not found.");
      return;
    }
    const locationUuid = location.uuid;
    if (!window.confirm(`Are you sure you want to delete the audio for field '${fieldType}'?`) || isReadOnly) {
      return;
    }
    try {
      const response = await fetch(`/api/audio/${locationUuid}/${fieldType}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete audio');
      }
      setQuestAudioFiles(prevAudio =>
        prevAudio.filter(audio =>
          !(audio.location_uuid === locationUuid && audio.field_type === fieldType)
        )
      );
      // Also remove from original audio state
      setQuestOriginalAudioFiles(prevAudio =>
        prevAudio.filter(audio =>
          !(audio.location_uuid === locationUuid && audio.field_type === fieldType)
        )
      );
      alert('Audio file deleted successfully');
    } catch (error) {
      console.error('Audio deletion error:', error);
      alert(`Failed to delete audio file: ${error.message}`);
    }
  }, [locations]);

  const handleAddAnswer = (locationId) => {
    if (!newAnswer.trim() || isReadOnly) return;
    
    // Store the current scroll position
    const container = locationsListRef.current;
    const scrollTop = container ? container.scrollTop : 0;
    
    const location = locations.find(loc => loc.id === locationId);
    if (!location || isReadOnly) return;
    
    const updatedAnswers = [...(location.answer_variations || []), newAnswer.trim()];
    onLocationUpdate(locationId, 'answer_variations', updatedAnswers);
    
    // Clear the input field
    setNewAnswer('');
    
    // Wait for next tick to ensure state is updated before saving
    setTimeout(() => {
      // Restore scroll position after state updates
      if (container) {
        container.scrollTop = scrollTop;
      }
      
      // Focus on the input field after scroll position is restored
      setTimeout(() => {
        if (newAnswerRef.current) {
          newAnswerRef.current.focus();
        }
      }, 50);
    }, 50);
  };

  // Optional numeric tolerance next to the exact variations (answer_numeric):
  // the app accepts any number within ±tolerance of value, or of value×scale
  // for "how many millions?"-style questions. Both fields empty → no tolerance.
  const handleAnswerNumericChange = (locationId, patch) => {
    if (isReadOnly) return;
    const location = locations.find(loc => loc.id === locationId);
    if (!location) return;
    const next = { unit: 'percent', scale: 1, ...(location.answer_numeric || {}), ...patch };
    const value = Number.isFinite(next.value) ? next.value : null;
    const tolerance = Number.isFinite(next.tolerance) ? next.tolerance : null;
    if (value === null && tolerance === null) {
      onLocationUpdate(locationId, 'answer_numeric', null);
      return;
    }
    onLocationUpdate(locationId, 'answer_numeric', {
      value,
      tolerance,
      unit: next.unit === 'abs' ? 'abs' : 'percent',
      scale: Number(next.scale) || 1,
    });
  };

  const handleRemoveAnswer = (locationId, index) => {
    // Store the current scroll position
    const locationElement = document.getElementById(`location-${locationId}`);
    const container = locationElement ? locationElement.closest('.locations-list') : null;
    const scrollTop = container ? container.scrollTop : 0;
    
    const location = locations.find(loc => loc.id === locationId);
    if (!location || isReadOnly) return;
    
    const updatedAnswers = [...(location.answer_variations || [])];
    updatedAnswers.splice(index, 1);
    onLocationUpdate(locationId, 'answer_variations', updatedAnswers);
    
    // Wait for next tick to ensure state is updated before saving
    setTimeout(() => {
      // Restore scroll position after state updates
      if (container) {
        container.scrollTop = scrollTop;
      }
    }, 10);
  };

  const handleDragStart = (e, index) => {
    if (isReadOnly) {
      e.preventDefault();
      return;
    }
    setDraggedAnswerIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Add some styling to the dragged element
    setTimeout(() => {
      e.target.classList.add('dragging');
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove('dragging');
    setDraggedAnswerIndex(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, locationId, targetIndex) => {
    e.preventDefault();
    
    if (draggedAnswerIndex === null || draggedAnswerIndex === targetIndex || isReadOnly) return;
    
    // Store the current scroll position
    const locationElement = document.getElementById(`location-${locationId}`);
    const container = locationElement ? locationElement.closest('.locations-list') : null;
    const scrollTop = container ? container.scrollTop : 0;
    
    const location = locations.find(loc => loc.id === locationId);
    if (!location || isReadOnly) return;
    
    const updatedAnswers = [...(location.answer_variations || [])];
    const [movedItem] = updatedAnswers.splice(draggedAnswerIndex, 1);
    updatedAnswers.splice(targetIndex, 0, movedItem);
    
    onLocationUpdate(locationId, 'answer_variations', updatedAnswers);
    
    setDraggedAnswerIndex(null);
    
    // Wait for next tick to ensure state is updated before saving
    setTimeout(() => {
      // Restore scroll position after state updates
      if (container) {
        container.scrollTop = scrollTop;
      }
    }, 10);
  };

  // Handle location reordering
  const handleMoveLocation = (locationId, direction) => {
    // Store the current scroll position
    const locationElement = document.getElementById(`location-${locationId}`);
    const container = locationElement ? locationElement.closest('.locations-list') : null;
    const scrollTop = container ? container.scrollTop : 0;
    
    onLocationReorder(locationId, direction);
    
    // Wait for next tick to ensure state is updated before saving
    setTimeout(() => {
      // Restore scroll position after state updates
      if (container) {
        container.scrollTop = scrollTop;
      }
    }, 10);
  };

  // Toggle position selection dropdown for a location
  const togglePositionDropdown = (locationId) => {
    setShowMoveToPosition(prevState => prevState === locationId ? null : locationId);
  };

  // Handle moving a location to a specific position between two other locations
  const handleMoveToPosition = (locationId, targetPosition) => {
    // Store the current scroll position
    const locationElement = document.getElementById(`location-${locationId}`);
    const container = locationElement ? locationElement.closest('.locations-list') : null;
    const scrollTop = container ? container.scrollTop : 0;
    
    // Call the reorder function with the specific target position
    onLocationReorder(locationId, 'position', targetPosition);
    
    // Hide the position dropdown
    setShowMoveToPosition(null);
    
    // Wait for next tick to ensure state is updated before saving
    setTimeout(() => {
      // Restore scroll position after state updates
      if (container) {
        container.scrollTop = scrollTop;
      }
    }, 10);
  };

  // Handle adding a new location
  const handleAddLocation = () => {
    onAddLocation();
    
    // Wait for next tick to ensure state is updated
    setTimeout(() => {
      // After adding a new location, scroll to the bottom
      if (locationsListRef.current) {
        locationsListRef.current.scrollTop = locationsListRef.current.scrollHeight;
      }
    }, 100);
  };

  // Handle removing a location
  const handleRemoveLocation = (locationId) => {
    // First show confirmation dialog
    setShowConfirmDelete(locationId);
  };

  // Confirm location deletion
  const confirmRemoveLocation = (locationId) => {
    onRemoveLocation(locationId);
    setShowConfirmDelete(null);
    
    // Wait for next tick to ensure state is updated before saving
    setTimeout(() => {
    }, 10);
  };

  // Cancel location deletion
  const cancelRemoveLocation = () => {
    setShowConfirmDelete(null);
  };

  if (!locations || locations.length === 0) {
    return (
      <div className="locations-container">
        <div className="locations-list" ref={locationsListRef}>
          <h2>Quest Locations</h2>
          <div>No locations available</div>
          {!isReadOnly && (
            <button 
              className="add-location-button"
              onClick={handleAddLocation}
            >
              Add New Location
            </button>
          )}
        </div>
      </div>
    );
  }

  // Render locations in their current array order

  return (
    <div className="locations-container">
      <div className={`locations-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <span>Locations</span>
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? '▶' : '◀'}
          </button>
        </div>
        {!sidebarCollapsed && (
          <div className="sidebar-items">
            {locations.map((location, index) => !lensPasses(location) ? null : (
              <div
                key={location.id}
                className={`sidebar-item ${selectedLocationId === location.id ? 'active' : ''}`}
                onClick={() => onLocationSelect(location.id)}
                title={location.name}
              >
                <span className="sidebar-item-number">{index + 1}</span>
                <span className="sidebar-item-name">{location.name}</span>
                {pipelinePointsByUuid && pipelinePointsByUuid[location.uuid] && (
                  <StageBadge
                    point={pipelinePointsByUuid[location.uuid]}
                    style={{ marginLeft: 6, padding: '0 5px', fontSize: 10, flexShrink: 0 }}
                  />
                )}
                {location.is_paid && (
                  <span title="Paid entry" style={{ fontSize: 10, marginLeft: 4, flexShrink: 0 }}>💰</span>
                )}
                {!(pipelinePointsByUuid && pipelinePointsByUuid[location.uuid]) && (location.tags || []).includes('AI-approved') && (
                  <span title="AI-approved (critic loop)" style={{ fontSize: 10, marginLeft: 4, flexShrink: 0 }}>✅</span>
                )}
              </div>
            ))}
            {!isReadOnly && (
              <button
                className="sidebar-add-location"
                onClick={handleAddLocation}
                title="Add a new location"
              >
                + Add location
              </button>
            )}
          </div>
        )}
      </div>
      <div className="locations-list" ref={locationsListRef}>
      {modalImage && (
        <ImageModal
          images={modalImage.images}
          startIndex={modalImage.index}
          onClose={() => setModalImage(null)}
        />
      )}

      <div className="locations-header">
        <h2>Quest Locations</h2>
        {lensFilteringPoints && (
          <span className="locations-filter-note">
            🔻 {lensVisibleLocations.length} of {locations.length} points — {lensFilterNote}
          </span>
        )}
      </div>
      {locations.map((location, index) => !lensPasses(location) ? null : (
        <div
          key={location.id}
          id={`location-${location.id}`}
          className={`location-item ${location.id === selectedLocationId ? 'selected' : ''}`}
        >
          {renderPipelineControls && renderPipelineControls(location)}
          <div className="location-header">
            <div className="location-reorder-buttons">
              <button 
                className="move-up-button"
                onClick={() => handleMoveLocation(location.id, 'up')}
                title="Move location up"
                disabled={index === 0 || isReadOnly}
              >
                ▲
              </button>
              <button 
                className="move-down-button"
                onClick={() => handleMoveLocation(location.id, 'down')}
                title="Move location down"
                disabled={index === locations.length - 1 || isReadOnly}
              >
                ▼
              </button>
              <button
                className="move-to-button"
                onClick={() => togglePositionDropdown(location.id)}
                title="Reorder this point or move it to another quest"
                disabled={isReadOnly}
              >
                ⋮
              </button>
              {showMoveToPosition === location.id && (
                <div className="position-dropdown">
                  <div className="position-dropdown-header">
                    <div className="position-dropdown-title">
                      {!isReadOnly &&
                      selectedTable !== 'quests' &&
                      questsEligibleForMove.length > 0 &&
                      onMoveLocationToQuest
                        ? 'Reorder & move'
                        : 'Move between positions'}
                    </div>
                    <button 
                      className="close-dropdown-button"
                      onClick={() => setShowMoveToPosition(null)}
                      title="Close"
                    >
                      ×
                    </button>
                  </div>
                  {!isReadOnly &&
                    selectedTable !== 'quests' &&
                    questsEligibleForMove.length > 0 &&
                    onMoveLocationToQuest ? (
                      <div className="move-to-quest-in-dropdown">
                        <span
                          className="move-to-quest-label"
                          id={`move-to-quest-label-${location.id}`}
                        >
                          To another quest
                        </span>
                        <select
                          aria-labelledby={`move-to-quest-label-${location.id}`}
                          className="move-to-quest-select"
                          value={moveToQuestSelections[location.id] || ''}
                          onChange={(e) =>
                            setMoveToQuestSelections((prev) => ({
                              ...prev,
                              [location.id]: e.target.value,
                            }))
                          }
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="">— choose quest —</option>
                          {questsEligibleForMove.map((q) => (
                            <option key={q.quest_id} value={q.quest_id}>
                              {q.quest_name || q.quest_id}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="move-to-quest-button"
                          disabled={!moveToQuestSelections[location.id]}
                          onClick={(e) => {
                            e.stopPropagation();
                            const target = moveToQuestSelections[location.id];
                            if (!target) return;
                            const questName =
                              questsEligibleForMove.find((q) => q.quest_id === target)
                                ?.quest_name || target;
                            if (
                              !window.confirm(
                                `Move «${location.name}» to «${questName}»? It will disappear from this quest.`
                              )
                            ) {
                              return;
                            }
                            setShowMoveToPosition(null);
                            onMoveLocationToQuest(location.uuid, target);
                          }}
                          title="Remove this point from the current quest and add it to the selected quest"
                        >
                          Move
                        </button>
                      </div>
                    ) : null}
                  {(!isReadOnly &&
                    selectedTable !== 'quests' &&
                    questsEligibleForMove.length > 0 &&
                    onMoveLocationToQuest) && (
                    <div className="position-dropdown-section-label">Within this quest</div>
                  )}
                  <div className="position-buttons">
                    <button 
                      key="beginning"
                      className="position-button position-special"
                      onClick={() => handleMoveToPosition(location.id, 0)}
                      disabled={index === 0}
                    >
                      ⟸ First
                    </button>
                    
                    {Array.from({ length: locations.length - 1 }, (_, i) => i).map((pos) => (
                      <button 
                        key={pos}
                        className="position-button"
                        onClick={() => handleMoveToPosition(location.id, pos)}
                        disabled={index === pos || index === pos + 1}
                      >
                        {pos + 1}-{pos + 2}
                      </button>
                    ))}
                    
                    <button 
                      key="end"
                      className="position-button position-special"
                      onClick={() => handleMoveToPosition(location.id, locations.length - 1)}
                      disabled={index === locations.length - 1}
                    >
                      Last ⟹
                    </button>
                  </div>
                </div>
              )}
            </div>
            <h3>
              <span className="location-number">{index + 1}</span>
              {taskCountsByLocation && tasksHref && taskCountsByLocation[location.uuid] && taskCountsByLocation[location.uuid].total > 0 ? (
                <a
                  className={`location-tasks-chip ${taskCountsByLocation[location.uuid].open ? 'has-open' : ''}`}
                  href={tasksHref(location.uuid)}
                  title={`${taskCountsByLocation[location.uuid].open} open of ${taskCountsByLocation[location.uuid].total} task(s) on this point — open in Tasks`}
                  aria-label={`Tasks of this point: ${taskCountsByLocation[location.uuid].open} open`}
                >
                  ✅ {taskCountsByLocation[location.uuid].open || taskCountsByLocation[location.uuid].total}
                </a>
              ) : null}
              <EditableFieldComponent
                locationId={location.id}
                field="name"
                value={location.name}
                multiline={false}
                isEditingThisField={editingField === `${location.id}-name`}
                images={getImagesFor(location.id, "name")}
                audioUrl={getAudioUrl(location.id, "name")}
                audioExists={hasAudio(location.id, "name")}
                audioSize={getAudioSize(location.id, "name")}
                downloadAudioUrl={getDownloadAudioUrl(location.id, "name")}
                originalAudioExists={hasOriginalAudio(location.id, "name")}
                originalAudioSize={getOriginalAudioSize(location.id, "name")}
                downloadOriginalAudioUrl={getDownloadOriginalAudioUrl(location.id, "name")}
                originalAudioFormatLabel={getOriginalAudioFormatLabel(location.id, "name")}
                selectedQuestId={selectedQuestId}
                onStartEditCallback={handleEditStart}
                onSaveEditCallback={handleSaveEdit}
                onImageUploadedCallback={localHandleImageUploaded}
                onAudioUploadedCallback={localHandleAudioUploaded}
                onVersionsChangedCallback={refreshAudioData}
                onDeleteImageCallback={localHandleDeleteImage}
              onEditImageCaptionCallback={localHandleEditImageCaption}
              onToggleImageBlurCallback={localHandleToggleImageBlur}
              onReorderImagesCallback={localHandleReorderImages}
                onDeleteAudioCallback={localHandleDeleteAudio}
                onThumbnailClickCallback={localHandleThumbnailClick}
                onGenerateVoiceoverCallback={onShowSpeechGeneration}
                isReadOnly={isReadOnly}
              />
            </h3>
            <div className="location-action-buttons">
              <div className="copy-button-wrapper location-export-menu-wrapper">
                <button
                  className="copy-location-json-button"
                  onClick={() => setExportMenuForId(exportMenuForId === location.id ? null : location.id)}
                  title="Copy / export this location"
                  aria-label="Location export menu"
                  aria-haspopup="menu"
                  aria-expanded={exportMenuForId === location.id}
                >
                  📋
                </button>
                {copiedLocationId === location.id && (
                  <span className="copy-toast-inline">Copied</span>
                )}
                {exportMenuForId === location.id && (
                  <div className="location-export-menu" role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { copyLocationJsonToClipboard(location); setExportMenuForId(null); }}
                    >
                      📋 Copy JSON
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setExportMenuForId(null); onExportLocationMarkdown && onExportLocationMarkdown(location, index); }}
                    >
                      📝 Export Markdown…
                    </button>
                  </div>
                )}
              </div>
              <button 
                className="remove-location-button"
                onClick={() => handleRemoveLocation(location.id)}
                title="Remove this location"
                aria-label="Remove location"
                disabled={isReadOnly}
              >
                ×
              </button>
            </div>
            {showConfirmDelete === location.id && (
              <div className="delete-confirmation-dialog">
                <p>Are you sure you want to delete this location?</p>
                <div className="delete-confirmation-buttons">
                  <button 
                    className="confirm-delete-button"
                    onClick={() => confirmRemoveLocation(location.id)}
                  >
                    Yes, delete
                  </button>
                  <button 
                    className="cancel-delete-button"
                    onClick={cancelRemoveLocation}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="location-details">
            {lensShow('meta') && (<>
            {/* UUID Display (read-only) - Moved before description */}
            <div className="location-uuid-container">
              <div className="location-uuid-item">
                <h4>UUID (read-only)</h4>
                <div 
                  className={`location-uuid ${copiedUuid === location.uuid ? 'copied' : ''}`}
                  onClick={() => copyUuidToClipboard(location.uuid)}
                  title="Click to copy"
                >
                  {location.uuid || 'Not assigned'}
                  {copiedUuid === location.uuid && <span className="copy-indicator">Copied!</span>}
                </div>
              </div>
              <div className="location-coordinates-item">
                <h4>Coordinates</h4>
                {editingLocationCoords === location.id ? (
                  <input
                    type="text"
                    value={coordsInput}
                    onChange={(e) => setCoordsInput(e.target.value)}
                    onBlur={() => handleCoordsSave(location.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCoordsSave(location.id);
                      } else if (e.key === 'Escape') {
                        setEditingLocationCoords(null);
                      }
                    }}
                    className="coordinates-input"
                    autoFocus
                    disabled={isReadOnly}
                  />
                ) : (
                  <div
                    className="location-coordinates"
                    onClick={() => handleCoordsEditStart(location)}
                    title="Click to edit coordinates"
                  >
                    {location.coordinates && location.coordinates.length === 2
                      ? `(${location.coordinates[0]}, ${location.coordinates[1]})`
                      : 'Not set'}
                  </div>
                )}
              </div>
            </div>
            
            {/* Location Type and Branching Logic */}
            <h4 className="location-type-header">Location Type</h4>
            <select
              value={location.type || 'ordinary'}
              onChange={(e) => onLocationUpdate(location.id, 'type', e.target.value)}
              className="location-type-selector"
              disabled={isReadOnly}
            >
              <option value="ordinary">Regular</option>
              <option value="pre-branching">Branching Point</option>
            </select>

            {location.type === 'pre-branching' && (
              <button
                type="button"
                className="branching-jump-button"
                onClick={() => {
                  const el = document.getElementById(`branching-section-${location.id}`);
                  fastScrollToElement(el);
                }}
                title="Branching Message is shown after the After Message — jump to its editor"
              >
                🔀 Edit Branching Message <span className="branching-jump-arrow">↓</span>
              </button>
            )}

            {/* Paid entry — feeds the auto-pipeline writer (a paid place means part
                of the audience stays outside, so the story must not lean on interior-only details). */}
            <h4 className="location-type-header">Paid entry</h4>
            <label
              className="paid-entry-toggle"
              style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: isReadOnly ? 'default' : 'pointer' }}
            >
              <input
                type="checkbox"
                checked={!!location.is_paid}
                onChange={(e) => onLocationUpdate(location.id, 'is_paid', e.target.checked)}
                disabled={isReadOnly}
              />
              <span>💰 Paid entry — the writer assumes some listeners stay outside</span>
            </label>

            <h4 className="tags-header">Tags</h4>
            <TagsEditor
              tags={location.tags || []}
              allQuestTags={allUniqueTags}
              users={usernames}
              assignees={tagAssigneesByLocation[location.uuid] || {}}
              onAddTag={(tag, assignees) => onAddTag(location.id, tag, assignees)}
              onRemoveTag={(tag) => onRemoveTag(location.id, tag)}
              onReassignTag={(tag, assignees) => onReassignTag(location.id, tag, assignees)}
            />
            </>)}
            {lensShow('comments') && (<>
            {(() => {
              const threadCount = (commentsByLocation[location.uuid] || []).filter(m => m.kind !== 'tag' && m.kind !== 'gen').length
                || ((typeof location.comment === 'string' && location.comment.trim()) ? 1 : 0);
              return (
                <h4
                  className="collapsible-header comment-header"
                  onClick={() => toggleCommentCollapse(location.id)}
                  title={isCommentCollapsed(location.id) ? "Click to expand comments" : "Click to collapse comments"}
                >
                  Comments{threadCount ? ` (${threadCount})` : ''} {isCommentCollapsed(location.id) ? '▶' : '▼'}
                </h4>
              );
            })()}
            <div className={`comment-field-wrapper ${isCommentCollapsed(location.id) ? 'collapsed' : ''}`}>
              <CommentThread
                messages={commentsByLocation[location.uuid] || []}
                legacyComment={(typeof location.comment === 'string' && location.comment.trim()) ? location.comment.trim() : ''}
                currentUsername={currentUsername}
                isReadOnly={isReadOnly}
                locationId={location.id}
                locationUuid={location.uuid}
                questId={selectedQuestId}
                onAdd={onAddComment}
                onEdit={onEditComment}
                onDelete={onDeleteComment}
              />
            </div>
            </>)}
            {lensShow('description') && (<>
            <h4 className="description-header">Description</h4>
            <EditableFieldComponent
              locationId={location.id}
              field="description"
              value={location.description}
              multiline={true}
              isEditingThisField={editingField === `${location.id}-description`}
              images={getImagesFor(location.id, "description")}
              appearanceImages={getImagesFor(location.id, "appearance_description")}
              videos={getVideosFor(location.id, "description")}
              onVideoChangedCallback={refreshQuestVideos}
              onEditVideoCaptionCallback={localHandleEditVideoCaption}
              onReorderVideosCallback={localHandleReorderVideos}
              audioUrl={getAudioUrl(location.id, "description")}
              audioExists={hasAudio(location.id, "description")}
              audioSize={getAudioSize(location.id, "description")}
              downloadAudioUrl={getDownloadAudioUrl(location.id, "description")}
              originalAudioExists={hasOriginalAudio(location.id, "description")}
              originalAudioSize={getOriginalAudioSize(location.id, "description")}
              downloadOriginalAudioUrl={getDownloadOriginalAudioUrl(location.id, "description")}
              originalAudioFormatLabel={getOriginalAudioFormatLabel(location.id, "description")}
              selectedQuestId={selectedQuestId}
              onStartEditCallback={handleEditStart}
              onSaveEditCallback={handleSaveEdit}
              onImageUploadedCallback={localHandleImageUploaded}
              onAudioUploadedCallback={localHandleAudioUploaded}
              onVersionsChangedCallback={refreshAudioData}
              onDeleteImageCallback={localHandleDeleteImage}
              onEditImageCaptionCallback={localHandleEditImageCaption}
              onToggleImageBlurCallback={localHandleToggleImageBlur}
              onReorderImagesCallback={localHandleReorderImages}
              onDeleteAudioCallback={localHandleDeleteAudio}
              onThumbnailClickCallback={localHandleThumbnailClick}
              onGenerateVoiceoverCallback={onShowSpeechGeneration}
              isReadOnly={isReadOnly}
              maxLength={4096}
            />
            </>)}
            {lensShow('task') && (<>
            <h4 className="task-header">Task</h4>
            <EditableFieldComponent
              locationId={location.id}
              field="task"
              value={location.task}
              multiline={true}
              isEditingThisField={editingField === `${location.id}-task`}
              images={getImagesFor(location.id, "task")}
              appearanceImages={getImagesFor(location.id, "appearance_task")}
              videos={getVideosFor(location.id, "task")}
              onVideoChangedCallback={refreshQuestVideos}
              onEditVideoCaptionCallback={localHandleEditVideoCaption}
              onReorderVideosCallback={localHandleReorderVideos}
              audioUrl={getAudioUrl(location.id, "task")}
              audioExists={hasAudio(location.id, "task")}
              audioSize={getAudioSize(location.id, "task")}
              downloadAudioUrl={getDownloadAudioUrl(location.id, "task")}
              originalAudioExists={hasOriginalAudio(location.id, "task")}
              originalAudioSize={getOriginalAudioSize(location.id, "task")}
              downloadOriginalAudioUrl={getDownloadOriginalAudioUrl(location.id, "task")}
              originalAudioFormatLabel={getOriginalAudioFormatLabel(location.id, "task")}
              selectedQuestId={selectedQuestId}
              onStartEditCallback={handleEditStart}
              onSaveEditCallback={handleSaveEdit}
              onImageUploadedCallback={localHandleImageUploaded}
              onAudioUploadedCallback={localHandleAudioUploaded}
              onVersionsChangedCallback={refreshAudioData}
              onDeleteImageCallback={localHandleDeleteImage}
              onEditImageCaptionCallback={localHandleEditImageCaption}
              onToggleImageBlurCallback={localHandleToggleImageBlur}
              onReorderImagesCallback={localHandleReorderImages}
              onDeleteAudioCallback={localHandleDeleteAudio}
              onThumbnailClickCallback={localHandleThumbnailClick}
              onGenerateVoiceoverCallback={onShowSpeechGeneration}
              isReadOnly={isReadOnly}
              maxLength={4096}
            />
            </>)}
            {lensShow('answers') && (<>
            <h4
              className="collapsible-header answers-header"
              onClick={() => toggleAnswersCollapse(location.id)}
              title={isAnswersCollapsed(location.id) ? "Click to expand answers" : "Click to collapse answers"}
            >
              Possible Answers {isAnswersCollapsed(location.id) ? '▶' : '▼'}
            </h4>
            <div className={`answer-variations ${isAnswersCollapsed(location.id) ? 'collapsed' : ''}`}>
              {(location.answer_variations || []).map((answer, index) => (
                <div 
                  key={index} 
                  className="answer-chip"
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, location.id, index)}
                >
                  <span className="drag-handle">≡</span>
                  <span className="answer-text">{answer}</span>
                  <span 
                    className="remove-answer" 
                    onClick={() => handleRemoveAnswer(location.id, index)}
                    title="Remove this answer"
                  >
                    ×
                  </span>
                </div>
              ))}
              <div className="add-answer-container">
                <input
                  type="text"
                  ref={newAnswerRef}
                  placeholder="Type new answer..."
                  value={newAnswer}
                  onChange={(e) => setNewAnswer(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAddAnswer(location.id);
                    }
                  }}
                  className="add-answer-input"
                  disabled={isReadOnly}
                />
                <button
                  onClick={() => handleAddAnswer(location.id)}
                  className="add-answer-button"
                  title="Add new answer"
                  disabled={isReadOnly}
                >
                  +
                </button>
                {/* Pipeline-draft only: ask the model for extra accepted
                    spellings (synonyms, other languages, word forms, translit,
                    typos) and auto-append the new ones. */}
                {!isReadOnly && pipelinePointsByUuid && location.uuid && pipelinePointsByUuid[location.uuid]
                  && (location.task || '').trim()
                  && (((location.answer_variations || []).some((a) => (a || '').trim())) || (location.answer || '').trim()) && (
                  <button
                    onClick={() => handleGenerateAnswerVariants(location)}
                    className="expand-answers-button"
                    title={`Expand accepted answers with ${modelKey(pipelineModels, 'answers') || 'the model'} (synonyms, other languages, word forms, typos) — appends new ones, never removes`}
                    disabled={!!answerGenBusy[location.uuid]}
                  >
                    {answerGenBusy[location.uuid] ? '…' : '✨'}
                  </button>
                )}
                {!isReadOnly && pipelinePointsByUuid && location.uuid && pipelinePointsByUuid[location.uuid]
                  && (location.task || '').trim()
                  && (((location.answer_variations || []).some((a) => (a || '').trim())) || (location.answer || '').trim()) && (
                  <ModelPicker k="answers" />
                )}
              </div>
              <div
                className="answer-numeric-row"
                title="Numeric tolerance: any number within ±tolerance of the value is accepted (also ×scale, e.g. 110 and 110 000 000 for “how many millions?”). Leave empty for exact answers only."
              >
                <span className="answer-numeric-label">≈ Numeric</span>
                <input
                  type="number"
                  className="answer-numeric-input"
                  placeholder="value"
                  value={location.answer_numeric && Number.isFinite(location.answer_numeric.value) ? location.answer_numeric.value : ''}
                  onChange={(e) => handleAnswerNumericChange(location.id, { value: e.target.value === '' ? null : Number(e.target.value) })}
                  disabled={isReadOnly}
                />
                <span className="answer-numeric-pm">±</span>
                <input
                  type="number"
                  className="answer-numeric-input"
                  placeholder="tol."
                  min="0"
                  value={location.answer_numeric && Number.isFinite(location.answer_numeric.tolerance) ? location.answer_numeric.tolerance : ''}
                  onChange={(e) => handleAnswerNumericChange(location.id, { tolerance: e.target.value === '' ? null : Number(e.target.value) })}
                  disabled={isReadOnly}
                />
                <select
                  className="answer-numeric-select"
                  value={(location.answer_numeric && location.answer_numeric.unit) || 'percent'}
                  onChange={(e) => handleAnswerNumericChange(location.id, { unit: e.target.value })}
                  disabled={isReadOnly}
                >
                  <option value="percent">%</option>
                  <option value="abs">abs</option>
                </select>
                <select
                  className="answer-numeric-select"
                  value={String((location.answer_numeric && location.answer_numeric.scale) || 1)}
                  onChange={(e) => handleAnswerNumericChange(location.id, { scale: Number(e.target.value) })}
                  disabled={isReadOnly}
                  title="Unit the question asks in: bare numbers are compared both as-is and multiplied by this"
                >
                  <option value="1">×1</option>
                  <option value="1000">×1 000</option>
                  <option value="1000000">×1 000 000</option>
                  <option value="1000000000">×1 000 000 000</option>
                </select>
                {!isReadOnly && location.answer_numeric && (
                  <span
                    className="remove-answer"
                    onClick={() => onLocationUpdate(location.id, 'answer_numeric', null)}
                    title="Remove numeric tolerance"
                  >
                    ×
                  </span>
                )}
              </div>
            </div>
            </>)}
            {lensShow('hint') && (<>
            {/* Always render the hint field, using empty string if not present */}
            <h4 className="hint-header">Hint</h4>
            <EditableFieldComponent
              locationId={location.id}
              field="hint"
              value={location.hint || ''}
              multiline={true}
              isEditingThisField={editingField === `${location.id}-hint`}
              images={getImagesFor(location.id, "hint")}
              audioUrl={getAudioUrl(location.id, "hint")}
              audioExists={hasAudio(location.id, "hint")}
              audioSize={getAudioSize(location.id, "hint")}
              downloadAudioUrl={getDownloadAudioUrl(location.id, "hint")}
              originalAudioExists={hasOriginalAudio(location.id, "hint")}
              originalAudioSize={getOriginalAudioSize(location.id, "hint")}
              downloadOriginalAudioUrl={getDownloadOriginalAudioUrl(location.id, "hint")}
              originalAudioFormatLabel={getOriginalAudioFormatLabel(location.id, "hint")}
              selectedQuestId={selectedQuestId}
              onStartEditCallback={handleEditStart}
              onSaveEditCallback={handleSaveEdit}
              onImageUploadedCallback={localHandleImageUploaded}
              onAudioUploadedCallback={localHandleAudioUploaded}
              onVersionsChangedCallback={refreshAudioData}
              onDeleteImageCallback={localHandleDeleteImage}
              onEditImageCaptionCallback={localHandleEditImageCaption}
              onToggleImageBlurCallback={localHandleToggleImageBlur}
              onReorderImagesCallback={localHandleReorderImages}
              onDeleteAudioCallback={localHandleDeleteAudio}
              onThumbnailClickCallback={localHandleThumbnailClick}
              onGenerateVoiceoverCallback={onShowSpeechGeneration}
              isReadOnly={isReadOnly}
            />
            </>)}
            {lensShow('after') && (<>
            <h4 className="after-message-header">After Message</h4>
            <EditableFieldComponent
              locationId={location.id}
              field="after_message"
              value={location.after_message}
              multiline={true}
              isEditingThisField={editingField === `${location.id}-after_message`}
              images={getImagesFor(location.id, "after_message")}
              appearanceImages={getImagesFor(location.id, "appearance_after_message")}
              videos={getVideosFor(location.id, "after_message")}
              onVideoChangedCallback={refreshQuestVideos}
              onEditVideoCaptionCallback={localHandleEditVideoCaption}
              onReorderVideosCallback={localHandleReorderVideos}
              audioUrl={getAudioUrl(location.id, "after_message")}
              audioExists={hasAudio(location.id, "after_message")}
              audioSize={getAudioSize(location.id, "after_message")}
              downloadAudioUrl={getDownloadAudioUrl(location.id, "after_message")}
              originalAudioExists={hasOriginalAudio(location.id, "after_message")}
              originalAudioSize={getOriginalAudioSize(location.id, "after_message")}
              downloadOriginalAudioUrl={getDownloadOriginalAudioUrl(location.id, "after_message")}
              originalAudioFormatLabel={getOriginalAudioFormatLabel(location.id, "after_message")}
              selectedQuestId={selectedQuestId}
              onStartEditCallback={handleEditStart}
              onSaveEditCallback={handleSaveEdit}
              onImageUploadedCallback={localHandleImageUploaded}
              onAudioUploadedCallback={localHandleAudioUploaded}
              onVersionsChangedCallback={refreshAudioData}
              onDeleteImageCallback={localHandleDeleteImage}
              onEditImageCaptionCallback={localHandleEditImageCaption}
              onToggleImageBlurCallback={localHandleToggleImageBlur}
              onReorderImagesCallback={localHandleReorderImages}
              onDeleteAudioCallback={localHandleDeleteAudio}
              onThumbnailClickCallback={localHandleThumbnailClick}
              onGenerateVoiceoverCallback={onShowSpeechGeneration}
              isReadOnly={isReadOnly}
              maxLength={4096}
            />
            </>)}

            {lensShow('branching') && location.type === 'pre-branching' && (
              <div className="branching-container" id={`branching-section-${location.id}`}>
                <h4 className="branching-message-header">Branching Message</h4>
                <EditableFieldComponent
                  locationId={location.id}
                  field="branching_message"
                  value={location.branching_message || ''}
                  multiline={true}
                  isEditingThisField={editingField === `${location.id}-branching_message`}
                  images={getImagesFor(location.id, "branching_message")}
                  audioUrl={getAudioUrl(location.id, "branching_message")}
                  audioExists={hasAudio(location.id, "branching_message")}
                  audioSize={getAudioSize(location.id, "branching_message")}
                  downloadAudioUrl={getDownloadAudioUrl(location.id, "branching_message")}
                  originalAudioExists={hasOriginalAudio(location.id, "branching_message")}
                  originalAudioSize={getOriginalAudioSize(location.id, "branching_message")}
                  downloadOriginalAudioUrl={getDownloadOriginalAudioUrl(location.id, "branching_message")}
                  originalAudioFormatLabel={getOriginalAudioFormatLabel(location.id, "branching_message")}
                  selectedQuestId={selectedQuestId}
                  onStartEditCallback={handleEditStart}
                  onSaveEditCallback={handleSaveEdit}
                  onImageUploadedCallback={localHandleImageUploaded}
                  onAudioUploadedCallback={localHandleAudioUploaded}
                  onVersionsChangedCallback={refreshAudioData}
                  onDeleteImageCallback={localHandleDeleteImage}
                  onEditImageCaptionCallback={localHandleEditImageCaption}
                  onToggleImageBlurCallback={localHandleToggleImageBlur}
                  onReorderImagesCallback={localHandleReorderImages}
                  onDeleteAudioCallback={localHandleDeleteAudio}
                  onThumbnailClickCallback={localHandleThumbnailClick}
                  onGenerateVoiceoverCallback={onShowSpeechGeneration}
                  isReadOnly={isReadOnly}
                />
                <h4 className="branches-header">Branches</h4>
                <div className="branches-list">
                  {(location.branches || []).map((branch, index) => (
                    <div key={index} className="branch-item">
                      <input
                        type="text"
                        placeholder="Button text"
                        value={branch.text || ''}
                        onChange={(e) => handleBranchUpdate(location.id, index, 'text', e.target.value)}
                        className="branch-text-input"
                        disabled={isReadOnly}
                      />
                      <select
                        value={branch.next_location_uuid || ''}
                        onChange={(e) => handleBranchUpdate(location.id, index, 'next_location_uuid', e.target.value)}
                        className="branch-target-select"
                        disabled={isReadOnly}
                      >
                        <option value="">Select Target...</option>
                        <option value="END">--- END QUEST ---</option>
                        {locations
                          .filter(l => l.id !== location.id)
                          .map(l => {
                            const idx2 = locations.findIndex(x => x.id === l.id);
                            return (
                              <option key={l.id} value={l.uuid}>
                                {idx2 + 1}. {l.name}
                              </option>
                            );
                          })}
                      </select>
                      <button
                        onClick={() => handleRemoveBranch(location.id, index)}
                        className="remove-branch-button"
                        title="Remove this branch"
                        disabled={isReadOnly}
                      >
                        -
                      </button>
                    </div>
                  ))}
                  <button onClick={() => handleAddBranch(location.id)} className="add-branch-button" disabled={isReadOnly}>
                    + Add Branch
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
      {lensFilteringPoints && lensVisibleLocations.length === 0 && (
        <div className="locations-filter-empty">
          No point matches the Lens filter — {lensFilterNote}
        </div>
      )}
      {/* Bottom boundary: real spacer so the last point isn't flush against the
          screen edge and far points have room to scroll up toward the top. */}
      <div className="locations-list-padding" aria-hidden="true" />
      </div>
    </div>
  );
};

export default LocationsList;